// WS-27: a ride with animals along a path (a reindeer or horse sleigh line). Generic, off unless the resort has
// `rides`. Schema, motion rules and checks: skill references/rides.md. Animals = the rigged glTF of
// animals.js; sleds and riders = vertex-coloured meshes; the herder = a person of lazy/folk.js (VIS-53); sleds are towed behind their animal;
// ropes = one thin lit mesh (a Line is 1 px wide at any distance and glows at night).
// WS-75 life: node materials (the animals' bones and the riding harness are posed by animals.js / instancing.js).
// WS-85: a ride on any path. RIDE_KINDS = the kinds of ride (their teams' defaults are the two live rides': the first
// client's reindeer raito, the second's husky team) and rideFromPath(...) = a whole resort.rides[] entry on a drawn line (Fine-tune's
// "Ride on this path", lazy/touches.js); a ride added so is built, viewed (resort.rideViews) and picked like a package
// one. A kind's animal comes from resort.animalModels, else the engine's own copy (web/models/animals/<animal>.glb).
// The animal sleigh's first guest is a child mesh 'guest' and the sled carries its seat (userData.sled): the ride view
// (lazy/rideview.js) sits in that guest's head with the figure hidden.
// A ride whose teams come from a lazy module (`teams.kind`: 'dogsled' = lazy/dogsled.js, 'snowmobile' =
// lazy/snowmobile.js, 'horsesleigh' / 'horseback' = lazy/horses.js, fetched by main.js only for such a ride) keeps it in
// `R.team` ({ teams: [{ sled, guest, driver, dogs | horses }], place(R, dt, pace), dispose() }): the ride view and Film read the
// first team's sled and guest the same way.
// VIS-267 step 3 (lifepace.js): every ride's LOGIC runs every frame wherever it is (its place on the line, speed, wait,
// give way, a convoy's queue, a towed sled's path, the spots the give way board and the ride view read); its POSE (the
// sleds', animals' and people's matrices and clips, the ropes) only when lifePace.pose gives its sphere time: a sleigh
// line here is one mover, a lazy module's team (or each snowmobile) one. Not posed = nothing written the renderer or
// rest.js can see. A pose the life pace does not choose (a model arriving, Film, the editor's place(), the ride the ride
// view is on: update(dt, viewed)) is told to it (FULL), so it knows where the mover is drawn.
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { derivedMaterials } from './materials.js'; // WS-80 C: a fence's bulbs at their own brightness
import { fenceBulbCount, stretchDist } from './lines/measure.js'; // VIS-197: how many bulbs a fence gets, the one count (the portal's Fences panel says the same)
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported
import { movers, RANK, STAND, BERTH } from './giveway.js'; // give way: rides give way to people, animals and each other
import { lifePace } from './lifepace.js'; // VIS-267 step 3: posed only in view, far small ones on the shared tick

const { hypot, atan2 } = Math; // (shorter as served)

// ---- vertex-coloured kit
const attr = (a, n = 3) => new THREE.BufferAttribute(a, n); // (one geometry attribute)
class VKit {
  constructor() { this.list = []; }
  add(geo, color) {
    const g = geo.index ? geo.toNonIndexed() : geo;
    g.deleteAttribute('uv');
    if (!g.attributes.normal) g.computeVertexNormals();
    const c = new THREE.Color(color), n = g.attributes.position.count, a = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { a[i * 3] = c.r; a[i * 3 + 1] = c.g; a[i * 3 + 2] = c.b; }
    g.setAttribute('color', attr(a));
    this.list.push(g);
    return g;
  }
  box(color, x, y, z, sx, sy, sz, ry = 0, rz = 0, rx = 0) {
    const g = new THREE.BoxGeometry(sx, sy, sz);
    if (rx) g.rotateX(rx);
    if (rz) g.rotateZ(rz);
    if (ry) g.rotateY(ry);
    return this.add(g.translate(x, y, z), color);
  }
  cyl(color, x, y, z, r0, r1, h, seg = 8, rz = 0, rx = 0) {
    const g = new THREE.CylinderGeometry(r0, r1, h, seg);
    if (rx) g.rotateX(rx);
    if (rz) g.rotateZ(rz);
    return this.add(g.translate(x, y, z), color);
  }
  sphere(color, x, y, z, r, sy = 1, seg = 10) { return this.add(new THREE.SphereGeometry(r, seg, Math.max(6, seg * 0.7 | 0)).scale(1, sy, 1).translate(x, y, z), color); }
  tube(color, pts, r = 0.025, seg = 24) {
    const curve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)));
    return this.add(new THREE.TubeGeometry(curve, seg, r, 6, false), color);
  }
  build() { return this.list.length ? mergeGeometries(this.list) : new THREE.BufferGeometry(); }
}
let vcolMat = null;
const vertexColorMaterial = () => (vcolMat ||= new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.85, side: THREE.DoubleSide }));

// ---- sled + people (local +x = forward)
const WOOD = '#3a302a', WOOD_DARK = '#231d19', STEEL = '#a7adb3', FUR = '#7d6a5a', FUR_PALE = '#d8ccbb', BLANKET = '#e7dfd2';
const JACKETS = ['#2b3550', '#d9d5e6', '#1e2024', '#7a2f2f', '#3d5a4c', '#5b4a6e'];
const HATS = ['#ece4d8', '#c7b39d', '#2a2a2c', '#b8433a', '#e2d7c4', '#3a4a6a'];
const WRAPS = [BLANKET, FUR, '#cfc5b6', FUR]; // what covers the guests' legs: wool blanket or a reindeer skin
const SKIN = '#e0b9a0', ROPE = '#8a3226', TRACE = '#4a3a2e'; // lead rope dull red, traces leather (photos)

/** A guest seated low in the sled, reclined; hip at (x, y, z) in the sled's frame. */
function rider(k, x, y, z, i) {
  const jacket = JACKETS[i % JACKETS.length], hat = HATS[(i * 3 + 1) % HATS.length];
  const lean = 0.42; // reclined against the low seat back
  const up = (d) => [x - Math.sin(lean) * d, y + Math.cos(lean) * d, z];
  k.add(new THREE.CapsuleGeometry(0.155, 0.26, 4, 10).scale(0.85, 1, 1).rotateZ(lean).translate(...up(0.24)), jacket); // torso
  const head = up(0.6);
  k.sphere(SKIN, head[0] + 0.015, head[1], head[2], 0.095, 1.08);
  if (i % 3 === 2) { // fur-trimmed hood
    k.sphere(jacket, head[0] - 0.02, head[1] + 0.01, head[2], 0.125, 1.05);
    k.add(new THREE.TorusGeometry(0.1, 0.035, 6, 14).rotateY(Math.PI / 2).translate(head[0] + 0.05, head[1], head[2]), FUR_PALE);
  } else { // knitted beanie with a pompom
    k.add(new THREE.SphereGeometry(0.104, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55).scale(1, 0.95, 1).translate(head[0] - 0.005, head[1] + 0.02, head[2]), hat);
    k.cyl(hat, head[0] - 0.005, head[1] + 0.005, head[2], 0.106, 0.106, 0.04, 12);
    k.sphere(i % 2 ? '#f2eee6' : hat, head[0] - 0.03, head[1] + 0.13, head[2], 0.042);
  }
  k.cyl(i % 2 ? '#8a2f2a' : '#d7d1c4', ...up(0.47), 0.085, 0.1, 0.07, 10); // scarf
}

/** Photo sled: low tub, runners curling up at the front, low seat back, skins. opts { riders: 0..2, blanket, seed, apart }.
 * Returns the geometry and the rail bulb positions; WS-85 `apart`: the guests are not in it but in `guests` (a geometry
 * each, the first on the right) and `seat` = the first guest's { seat (hip), eye } for the ride view. Origin = ground
 * under the middle, +x = forward. */
export function sledGeometry({ riders = 0, blanket = true, seed = 0, apart = false } = {}) {
  const k = new VKit(), lights = [], guests = [];
  const zr = 0.34;
  for (const s of [1, -1]) { // runners: steel shoe on a wooden runner, the front sweeping up and curling back (photos)
    const run = [[-1.08, 0.06, s * zr], [-0.9, 0.035, s * zr], [0.5, 0.035, s * zr], [0.86, 0.08, s * zr], [1.06, 0.26, s * zr * 0.95], [1.1, 0.5, s * zr * 0.9], [1.0, 0.64, s * zr * 0.86], [0.88, 0.6, s * zr * 0.86]];
    k.tube(WOOD_DARK, run, 0.032, 40);
    k.tube(STEEL, run.slice(0, 5).map(([a, b, c]) => [a, b - 0.025, c]), 0.016, 24);
    for (const x of [-0.8, -0.3, 0.2, 0.62]) k.box(WOOD_DARK, x, 0.13, s * zr, 0.05, 0.16, 0.05); // stanchions
    // low sides, narrowing to the front
    k.box(WOOD, -0.2, 0.3, s * (zr + 0.01), 1.4, 0.2, 0.035);
    const fa = [0.5, s * (zr + 0.01)], fb = [0.86, s * 0.2], len = hypot(fb[0] - fa[0], fb[1] - fa[1]);
    k.box(WOOD, (fa[0] + fb[0]) / 2, 0.3, (fa[1] + fb[1]) / 2, len, 0.2, 0.035, -atan2(fb[1] - fa[1], fb[0] - fa[0]));
    k.box(WOOD_DARK, -0.15, 0.41, s * (zr + 0.015), 1.5, 0.035, 0.06); // top rail
  }
  k.box(WOOD, -0.12, 0.21, 0, 1.75, 0.035, zr * 2 + 0.04); // floor (seated low)
  k.box(WOOD, 0.86, 0.32, 0, 0.04, 0.24, 0.42); // bow board
  // seat back
  const bx = -0.9, lean = 0.42, bh = 0.5;
  for (let z = -zr; z <= zr + 1e-6; z += zr / 3) k.box(WOOD, bx - Math.sin(lean) * bh / 2, 0.21 + Math.cos(lean) * bh / 2, z, 0.035, bh, 0.06, 0, lean);
  k.box(WOOD_DARK, bx - Math.sin(lean) * bh, 0.21 + Math.cos(lean) * bh, 0, 0.06, 0.06, zr * 2 + 0.1, 0, lean);
  // reindeer skins
  k.box(FUR, bx - Math.sin(lean) * 0.24 + 0.05, 0.23 + Math.cos(lean) * 0.24, 0, 0.06, 0.5, zr * 2 - 0.04, 0, lean);
  k.box(FUR_PALE, bx - Math.sin(lean) * bh + 0.05, 0.23 + Math.cos(lean) * bh, 0, 0.07, 0.07, zr * 2 - 0.02, 0, lean);
  k.box(FUR, -0.45, 0.24, 0, 0.95, 0.05, zr * 2 - 0.04);
  for (const s of [1, -1]) k.box(FUR, -0.5, 0.36, s * (zr + 0.04), 0.7, 0.18, 0.025); // skin hanging over the side
  for (let i = 0; i < riders; i++) rider(apart ? (guests[i] = new VKit()) : k, -0.68, 0.26, riders === 1 ? 0 : (i ? -1 : 1) * 0.165, seed + i);
  if (riders) { // wrapped to the chest: one big blanket or skin over both guests' legs
    const wrap = blanket ? WRAPS[seed % WRAPS.length] : FUR;
    k.add(new THREE.CapsuleGeometry(0.2, 0.75, 4, 12).rotateZ(Math.PI / 2 - 0.12).scale(1, 0.75, riders === 1 ? 1.05 : 1.6).translate(-0.12, 0.38, 0), wrap);
    k.add(new THREE.SphereGeometry(0.22, 12, 8).scale(0.8, 0.9, riders === 1 ? 1.1 : 1.65).translate(-0.5, 0.45, 0), wrap); // tucked up to the chest
  }
  for (const s of [1, -1]) for (let x = -0.85; x <= 0.5; x += 0.12) lights.push([x, 0.45, s * (zr + 0.03)]);
  for (let z = -zr; z <= zr; z += 0.1) lights.push([bx - Math.sin(lean) * (bh + 0.04), 0.21 + Math.cos(lean) * (bh + 0.04), z]);
  const z = riders === 1 ? 0 : 0.165; // WS-85: the eye a little forward of the first guest's head (hip + 0.6 m along the 0.42 rad recline)
  return { geometry: k.build(), lights, guests: guests.map((q) => q.build()), seat: riders ? { seat: [-0.68, 0.26, z], eye: [-0.86, 0.83, z] } : null };
}

/**
 * VIS-53: the herder who leads the sleigh line on foot, one of the map's people (tools/animals/human.mjs `herder`, the
 * owner's pick of 2026-10-24; web/models/people/person-herder.glb): a rigged figure in its gakti, drawn by lazy/folk.js
 * in the people's instanced draw (+ its shadow), walk / idle by its own ground speed, the lead rope in its right hand.
 * `folk` = main.js's fetch of lazy/folk.js. Until the model is in (or without folk), `g` is an empty stand-in the ride
 * places and `hand` is null (no lead rope). Returns { g, hand, person, update(dt, v, film), dispose }.
 */
let herderModel = null;
function createHerder(folk, scene, instancing, R) {
  const G = {
    g: new THREE.Object3D(), hand: null, person: null, inst: null,
    update(dt, v, F) {
      const p = G.person;
      if (!p) return;
      if (F) p.pose('walk', Math.min(1, F.v / 0.4), F.d, F.t); // VIS-265 Film: the stride from the metres gone, no history
      else { p.setMode(v > 0.05 ? 'walk' : 'idle'); p.update(dt, v); }
    },
    dispose() { if (G.person) G.inst?.remove?.(G.person); }, // (lazy/fine.js dropRide: out of the shared people instancer)
  };
  folk?.then(async (F) => {
    const model = await (herderModel ||= F.loadPerson('person-herder'));
    if (R.removed) return;
    G.inst = F.peopleInstancer(scene, instancing);
    const one = () => 0.5, p = F.createPerson(model, F.lookColors(model.meta, one), one); // (one look: extras.looks)
    p.paced = true; // VIS-267 step 3: updated only when the ride is posed, with all the time owed (lazy/folk.js)
    p.object.position.copy(G.g.position); p.object.rotation.copy(G.g.rotation);
    G.g = p.object; G.hand = p.hand; G.person = p;
    scene.add(p.object); G.inst.add(p, model); R.objs.push(p.object); p.object.visible = R.active;
  }).catch((err) => { console.warn('ride herder failed:', err); degraded('people', err); });
  return G;
}

// ---- path sampling
const wrapA = (a) => atan2(Math.sin(a), Math.cos(a));
/** A polyline of Vector3 in n(length) equal steps; out.len = its length. */
function even(pts, n) {
  const L = [0], out = [];
  for (let i = 1; i < pts.length; i++) L.push(L[i - 1] + pts[i].distanceTo(pts[i - 1]));
  const len = L[L.length - 1], N = n(len);
  for (let i = 0, j = 1; i <= N; i++) {
    const d = (len * i) / N;
    while (j < L.length - 1 && L[j] < d) j++;
    out.push(pts[j - 1].clone().lerp(pts[j], (d - L[j - 1]) / (L[j] - L[j - 1] || 1)));
  }
  out.len = len;
  return out;
}
/** Route sampled for the ride: cfg { path, closed, direction ('cw' | 'ccw': driving sense seen from above, north up;
 * absent = as drawn), smooth }. Returns { N, len, closed, x, z, y, at(s), nearest([x, z]), step }.
 * WS-69 Life: exported so a route edited in Fine-tune is sampled exactly as the package's own. */
export function samplePath(cfg, heightAt) {
  let line = cfg.path;
  if (cfg.direction === 'cw' || cfg.direction === 'ccw') { // driving sense seen from above (north up)
    let ar = 0; for (let i = 0; i < line.length; i++) { const [x0, z0] = line[i], [x1, z1] = line[(i + 1) % line.length]; ar += x0 * -z1 - x1 * -z0; }
    if ((ar > 0) !== (cfg.direction === 'ccw')) line = line.slice().reverse();
  }
  const closed = cfg.closed !== false, ds = 0.5, fine = 0.25;
  const curve = new THREE.CatmullRomCurve3(line.map(([x, z]) => new THREE.Vector3(x, 0, z)), closed, 'centripetal', 0.5);
  // even points, corners rounded (1-2-1 passes ~ a Gaussian of `smooth` m), even again
  let P = even(curve.getPoints(line.length * 24), (l) => Math.round(l / fine));
  if (closed) P.pop();
  for (let k = Math.round(2 * ((cfg.smooth ?? 1.2) / fine) ** 2), n = P.length; k > 0; k--) {
    const Q = P.map((p) => p.clone());
    for (let i = closed ? 0 : 1; i < (closed ? n : n - 1); i++) P[i].copy(Q[(i + n - 1) % n]).add(Q[(i + 1) % n]).multiplyScalar(0.25).addScaledVector(Q[i], 0.5);
  }
  if (closed) P.push(P[0]);
  P = even(P, (l) => Math.max(8, Math.round(l / ds)));
  const len = P.len;
  if (closed) P.pop();
  const N = P.length, step = len / (closed ? N : N - 1);
  const F = () => new Float32Array(N), x = F(), z = F(), y = F(), hd = F();
  for (let i = 0; i < N; i++) { x[i] = P[i].x; z[i] = P[i].z; }
  const raw = Array.from({ length: N }, (_, i) => heightAt(x[i], z[i]));
  const idx = (i) => (closed ? ((i % N) + N) % N : Math.max(0, Math.min(N - 1, i)));
  for (let i = 0; i < N; i++) {
    let s = 0; for (let j = -3; j <= 3; j++) s += raw[idx(i + j)]; y[i] = s / 7;
    const p = idx(i - 1), q = idx(i + 1);
    hd[i] = atan2(-(z[q] - z[p]), x[q] - x[p]); // rotation.y for a +x-forward model
  }
  const at = (s, out = {}) => { // position, heading, curvature (1 / radius) at arc length s, all continuous
    const u = (closed ? ((s % len) + len) % len : Math.max(0, Math.min(len, s))) / step;
    const i0 = Math.floor(u), f = u - i0, a = idx(i0), b = idx(i0 + 1), dh = wrapA(hd[b] - hd[a]);
    out.x = x[a] + (x[b] - x[a]) * f; out.z = z[a] + (z[b] - z[a]) * f; out.y = y[a] + (y[b] - y[a]) * f;
    out.h = hd[a] + dh * f; out.k = Math.abs(dh) / step;
    return out;
  };
  const nearest = ([px, pz]) => { let best = 0, d = Infinity; for (let i = 0; i < N; i++) { const e = (x[i] - px) ** 2 + (z[i] - pz) ** 2; if (e < d) { d = e; best = i; } } return best * step; };
  return { N, len, closed, x, z, y, at, nearest, step };
}

// ---- pace (the owner, 2026-10-25): a horse ride trots on a long, nearly straight stretch and walks elsewhere, the
// same every lap: a run of at least `len` m whose heading turns less than 1 / `radius` per m over +-`window` m, `yard`
// m clear of the start (it leaves and comes in at a walk). Forest or not: his rides run through forest almost all the
// way, and the map cannot tell a wide forest track from a narrow one (on the first client's rides, trotting only away
// from trees left ~80 m of 3.2 km; straight runs of 40 m on a 25 m radius, ~85 % of both rides)
export const PACE = { len: 150, radius: 80, window: 5, yard: 15 };
/** Target speed (m/s) at each sample of route T (samplePath): `trot` on each straight run (PACE), `walk` elsewhere,
 *  eased down at `brake` m/s2 before a run ends so it is back at a walk there (speeding up is the ride's own `accel`).
 *  `sStart`: the start's arc length. */
export function paceProfile(T, { walk, trot, brake, sStart = 0 }) {
  const { N, step, len, closed } = T, v = new Float32Array(N).fill(walk);
  if (!(trot > walk)) return v;
  const w = PACE.window, P = {}, ok = new Uint8Array(N);
  for (let i = 0; i < N; i++) {
    const s = i * step;
    if (!closed && (s < w || s > len - w)) continue;
    const h0 = T.at(s - w, P).h, h1 = T.at(s + w, P).h;
    if (Math.abs(wrapA(h1 - h0)) > (2 * w) / PACE.radius) continue; // a bend
    const d = closed ? Math.min(((s - sStart) % len + len) % len, ((sStart - s) % len + len) % len) : Math.abs(s - sStart);
    if (d < PACE.yard) continue; // the start
    ok[i] = 1;
  }
  // runs of `ok` at least PACE.len long (a closed route: a run across its seam counts whole)
  const runs = [];
  let first = closed && ok[0] && ok[N - 1] ? ok.indexOf(0) : 0; // (a closed route: start after the run on its seam)
  if (first < 0) first = 0; // (all of it straight: one run round)
  for (let k = 0; k < N; k++) {
    const i = (first + k) % N;
    if (!ok[i]) continue;
    let n = 0; while (n < N - k && ok[(first + k + n) % N]) n++;
    if (n * step >= PACE.len) runs.push([i, n]);
    k += n - 1;
  }
  for (const [i, n] of runs) for (let k = 0; k < n; k++) v[(i + k) % N] = trot;
  // back to a walk where each run ends: no faster than it can still brake to the speed ahead (twice round a loop)
  for (let k = (closed ? 2 * N : N) - 2; k >= 0; k--) {
    const i = k % N, j = closed ? (i + 1) % N : i + 1;
    v[i] = Math.min(v[i], Math.sqrt(v[j] * v[j] + 2 * brake * step));
  }
  return v;
}

let trailTex = null; // WS-69 Life: one texture for every trail (a trail rebuilt while a route is dragged makes no new one)
function trailTexture() {
  if (trailTex) return trailTex;
  const c = document.createElement('canvas'); c.width = 64; c.height = 256;
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 0, 64, 0);
  grd.addColorStop(0, 'rgba(188,198,216,0)'); grd.addColorStop(0.16, 'rgba(188,198,216,0.85)'); grd.addColorStop(0.5, 'rgba(176,186,206,0.95)');
  grd.addColorStop(0.84, 'rgba(188,198,216,0.85)'); grd.addColorStop(1, 'rgba(188,198,216,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 256);
  let s = 3; const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 320; i++) { g.fillStyle = `rgba(120,132,158,${0.1 + rnd() * 0.16})`; g.beginPath(); g.ellipse(22 + rnd() * 20, rnd() * 256, 1.5 + rnd() * 2, 1.2 + rnd() * 2, 0, 0, 7); g.fill(); } // hoof prints
  for (const u of [17, 47]) { g.fillStyle = 'rgba(110,122,150,0.6)'; g.fillRect(u - 1, 0, 3, 256); g.fillStyle = 'rgba(255,255,255,0.45)'; g.fillRect(u + 2, 0, 1, 256); } // runner grooves
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace; tex.wrapT = THREE.RepeatWrapping; tex.anisotropy = 8;
  return (trailTex = tex);
}
/** Packed-snow lane on the terrain (runner grooves, hoof prints), one draw call. */
export function trailMesh(T, width, heightAt) { // (WS-69: exported for lazy/fine.js)
  const tex = trailTexture();
  const pos = [], uv = [], index = [], rows = T.closed ? T.N + 1 : T.N, hw = width / 2;
  for (let r = 0; r < rows; r++) {
    const i = r % T.N, a = (i - 1 + T.N) % T.N, b = (i + 1) % T.N;
    const tx = T.x[b] - T.x[a], tz = T.z[b] - T.z[a], l = hypot(tx, tz) || 1, nx = -tz / l, nz = tx / l;
    for (const o of [-hw, -hw / 3, hw / 3, hw]) {
      const px = T.x[i] + nx * o, pz = T.z[i] + nz * o;
      pos.push(px, heightAt(px, pz) + 0.14, pz); uv.push((o + hw) / width, (r * T.step) / 6);
    }
  }
  for (let r = 0; r < rows - 1; r++) for (let k = 0; k < 3; k++) { const a = r * 4 + k, b = a + 4; index.push(a, b, a + 1, a + 1, b, b + 1); }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setIndex(index); geo.computeVertexNormals();
  const m = new THREE.Mesh(geo, new THREE.MeshStandardNodeMaterial({ map: tex, transparent: true, depthWrite: false, roughness: 0.95, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
  m.receiveShadow = true; m.renderOrder = 2; m.name = 'ride-trail';
  return m;
}

// ---- fences (generic styles + gates)
/** Fence along a ring. cfg { style: 'rail' | 'roundpole' (pole panels, alternating in and out) | 'slanted' (the
 * Lappish riukuaita: thin poles leaning along the line, every `poleGap` m, `lean` deg off the ground, their tips above
 * the post pairs that hold them), height, rails, spacing, gates: [{ at, width, lights (metres of bulbs either side), draw (false: the opening only) }],
 * bulbs: { spacing } (WS-62: warm bulbs along the top rail of every run, one more draw call; VIS-188: evenly along the whole
 * fence, lines/measure.js fenceBulbCount; (fence light kinds) `type`, `sag`: FENCE_LIGHTS), bulbsOnly (WS-89 round 2: only the bulbs' mesh) }.
 * `ring` may also be a list of lines (WS-62: several open fences in one group, e.g. the lit fences along a resort's roads).
 * Returns a Group (2-4 draw calls). */
/** WS-89: the styles buildFence draws, their defaults, and the rails each can have (the Fine-tune panel and made fences
 *  keep `rails` within minRails..maxRails; slanted has none: its poles lean between post pairs). */
export const FENCE_STYLES = Object.freeze({
  rail: Object.freeze({ height: 1.25, rails: 3, spacing: 2.6, minRails: 1, maxRails: 5 }),
  roundpole: Object.freeze({ height: 1.6, rails: 6, spacing: 3.4, minRails: 2, maxRails: 8 }),
  slanted: Object.freeze({ height: 1.4, rails: 0, spacing: 1.8, minRails: 0, maxRails: 0 }),
});
/**
 * VIS-152: a fence's `posts: "spaced"`: its posts every `spacing` m measured ALONG the line, not one at every vertex (a
 * line that follows a road is resampled every metre, fenceroad.js, so it had a post every metre). The runs (`[ax, az,
 * bx, bz]` in order) are joined into stretches; a stretch ends where the line ends, at a gate (the next run does not
 * start where this one ends) and at a corner sharper than `corner` degrees, which keep their posts. Each stretch is cut
 * into round(length / spacing) equal pieces (at least one); the result is the runs between consecutive posts (chords).
 * Without the key the posts stand at every vertex as before (Aurora's made fences: VIS-152 checked they would move).
 */
export function spacedRuns(runs, spacing, corner = 30) {
  const out = [], cosMax = Math.cos((corner * Math.PI) / 180);
  let pts = null, last = null;
  const flush = () => {
    if (!pts || pts.length < 2) return;
    const cum = [0];
    for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
    const L = cum[cum.length - 1], n = Math.max(1, Math.round(L / spacing));
    const at = (d) => { let i = 1; while (i < cum.length - 1 && cum[i] < d) i++; const t = (d - cum[i - 1]) / Math.max(1e-9, cum[i] - cum[i - 1]); return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t]; };
    let p = pts[0];
    for (let k = 1; k <= n; k++) { const q = k === n ? pts[pts.length - 1] : at((k * L) / n); out.push([p[0], p[1], q[0], q[1]]); p = q; }
  };
  for (const [ax, az, bx, bz] of runs) {
    const len = hypot(bx - ax, bz - az);
    if (len < 1e-6) continue;
    const ux = (bx - ax) / len, uz = (bz - az) / len;
    const joined = last && hypot(ax - last.x, az - last.z) < 1e-4 && ux * last.ux + uz * last.uz >= cosMax;
    if (!joined) { flush(); pts = [[ax, az]]; }
    pts.push([bx, bz]);
    last = { x: bx, z: bz, ux, uz };
  }
  flush();
  return out;
}
/** WS-89: the height (m above the ground) of rail `r` (0 = the lowest) of `nR` on a fence `H` m high: one function for
 *  the rails and for the bulbs on the top rail (fenceTop). A rail fence's top rail is at H (3 rails: 0.45 / 0.85 / H as
 *  every fence before, below 0.95 m spread evenly); a round-pole fence spreads its poles from 0.22 m to H - 0.08. */
export function railHeight(style, r, nR, H) {
  if (style === 'roundpole') return nR < 2 ? H - 0.08 : 0.22 + (r * (H - 0.3)) / (nR - 1); // (one pole: at the top, was 0 / 0)
  if (nR === 3 && H > 0.95) return [0.45, 0.85, H][r];
  const lo = Math.min(0.45, H * 0.36);
  return nR < 2 ? H : lo + (r * (H - lo)) / (nR - 1);
}
/** WS-89: what a fence's bulbs sit on (m above the ground; a bulb's centre is 0.08 m above it): the top rail, or for
 *  slanted the tops of its post pairs (posts 0.82 x (H + 0.35) high, from 0.05 m under the ground). */
export const fenceTop = (style, nR, H) => (style === 'slanted' ? 0.82 * (H + 0.325) - 0.05 - 0.06 : railHeight(style, nR - 1, nR, H));
/** (fence light kinds, the owner 2026-10-31) What a fence's lights are (`bulbs.type`; none = `bulbs`): bulbs on the top
 *  rail every `spacing` m; fairy lights and festoon bulbs hung from post to post, drooping `sag` x the span (`bulbs.sag`,
 *  0-0.2 as a light line's strings: lazy/touches.js LIGHT_TYPES; kept here too: this file is in every visitor's page),
 *  `lift` m over the top rail at the posts, a bulb every `gap` m (festoon: hanging `hang` m under the cable); icicle
 *  strands under the top rail every `gap` m, 1-4 bulbs, up to `drop` m long (Lumo's boardwalk railing, photo 'Property -
 *  69'); a lamp on top of every post about `spacing` m apart. `r` a bulb's size; `share` the light one casts on the snow,
 *  x a fence bulb's (one about every 1.1 m): a lit metre lights the snow about as bulbs do (`userData.bulbShare`);
 *  `glow` a bulb's own brightness x the lamp's (icicles: a curtain of them would wash out in the glow). */
export const FENCE_LIGHTS = Object.freeze({
  bulbs: { r: 0.055, share: 1 },
  fairy: { gap: 0.15, sag: 0.04, lift: 0.13, r: 0.028, share: 0.15 / 1.1 },
  festoon: { gap: 0.5, sag: 0.06, lift: 0.12, hang: 0.05, r: 0.04, share: 0.5 / 1.1 },
  icicle: { gap: 0.12, drop: 0.45, r: 0.024, share: 0.15 / 1.1, glow: 0.45 },
  post: { r: 0.07, share: 4 },
});
/** A fence lights touch's look, kept: `type` (a FENCE_LIGHTS key) and a string's `sag` (0-0.2). lazy/ways.js,
 *  touches.js and madefence.js keep these two; the editor's doc.ts the same. */
export const fenceLightLook = (B) => ({ ...(FENCE_LIGHTS[B?.type] && Object.hasOwn(FENCE_LIGHTS, B.type) ? { type: B.type } : {}), ...(Number.isFinite(B?.sag) ? { sag: Math.min(0.2, Math.max(0, B.sag)) } : {}) });
/** The parts fence lights are made of, once: an 8-sided bulb (6 corners: a fairy or icicle bulb is a few pixels), a round
 *  bulb and a post lamp's box, each indexed with normals only (M.lamp reads no uv). */
let LIGHT_PARTS = null;
const lightParts = () => (LIGHT_PARTS ||= (() => {
  const part = (g) => ({ p: g.attributes.position.array, n: g.attributes.normal.array, i: g.index.array });
  const oct = new THREE.BufferGeometry(), P = [1, 0, 0, -1, 0, 0, 0, 1, 0, 0, -1, 0, 0, 0, 1, 0, 0, -1];
  oct.setAttribute('position', new THREE.Float32BufferAttribute(P, 3)).setAttribute('normal', new THREE.Float32BufferAttribute(P, 3));
  oct.setIndex([0, 2, 4, 4, 2, 1, 1, 2, 5, 5, 2, 0, 4, 3, 0, 1, 3, 4, 5, 3, 1, 0, 3, 5]);
  return { oct: part(oct), ball: part(new THREE.SphereGeometry(1, 6, 4)), box: part(new THREE.BoxGeometry(1, 1, 1)) };
})());
/** One geometry of many small lights: `list` = [part, x, y, z, sx, sy, sz] (a part of lightParts scaled, then moved). */
function lightsGeometry(list) {
  let nv = 0, ni = 0;
  for (const [q] of list) { nv += q.p.length / 3; ni += q.i.length; }
  const pos = new Float32Array(nv * 3), nor = new Float32Array(nv * 3), idx = new (nv > 65535 ? Uint32Array : Uint16Array)(ni);
  let v = 0, k = 0;
  for (const [q, x, y, z, sx, sy, sz] of list) {
    for (let j = 0; j < q.i.length; j++) idx[k++] = q.i[j] + v;
    for (let j = 0; j < q.p.length; j += 3, v++) {
      pos[v * 3] = x + q.p[j] * sx; pos[v * 3 + 1] = y + q.p[j + 1] * sy; pos[v * 3 + 2] = z + q.p[j + 2] * sz;
      nor[v * 3] = q.n[j]; nor[v * 3 + 1] = q.n[j + 1]; nor[v * 3 + 2] = q.n[j + 2];
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', attr(pos)).setAttribute('normal', attr(nor)).setIndex(attr(idx, 1));
  return g;
}
export function buildFence(M, heightAt, ring, cfg = {}, closed = true) {
  cfg = { ...M.fenceLook, ...cfg }; // WS-63 materials: the map's fence defaults (resort.materials.fence, lazy/matlook.js) under the fence's own keys
  const style = cfg.style || 'rail', S = FENCE_STYLES[style] || FENCE_STYLES.rail, H = cfg.height ?? S.height; // WS-89: the defaults in FENCE_STYLES
  const nR = cfg.rails ?? S.rails, spacing = cfg.spacing ?? S.spacing;
  const gates = (cfg.gates || []).map((g) => ({ ...g, at: [...g.at], width: g.width ?? 3.2 }));
  const posts = [], rails = [], gateGeo = [], bulbs = [];
  const only = !!cfg.bulbsOnly; // (WS-89 round 2) the bulbs alone (lazy/fine.js: a lights-only change keeps the posts and rails as built)
  const sawn = cfg.profile === 'sawn'; // WS-63 materials: rail profile round (default) | sawn (flat boards on square posts)
  // runs between gates: a gate cuts its edge at centre +- width/2
  const runs = [];
  let lineLen = 0; // WS-81: the lines' whole length (fenceBulbCount)
  for (const line of Array.isArray(ring[0]?.[0]) ? ring : [ring]) { // WS-62: a list of lines = open fences, one group
  const n = line.length, shut = closed && line === ring;
  for (let i = 0; i < (shut ? n : n - 1); i++) {
    const a = line[i], b = line[(i + 1) % n], len = hypot(b[0] - a[0], b[1] - a[1]);
    lineLen += len;
    if (len < 1e-3) continue;
    const ux = (b[0] - a[0]) / len, uz = (b[1] - a[1]) / len, cuts = [];
    for (const g of gates) {
      const t = (g.at[0] - a[0]) * ux + (g.at[1] - a[1]) * uz, d = Math.abs((g.at[0] - a[0]) * -uz + (g.at[1] - a[1]) * ux);
      if (d < 4 && t > g.width / 2 && t < len - g.width / 2) { cuts.push([t - g.width / 2, t + g.width / 2]); g.dir = [ux, uz]; g.at = [a[0] + ux * t, a[1] + uz * t]; }
    }
    cuts.sort((p, q) => p[0] - q[0]);
    let t0 = 0;
    for (const [c0, c1] of cuts) { runs.push([a[0] + ux * t0, a[1] + uz * t0, a[0] + ux * c0, a[1] + uz * c0]); t0 = c1; }
    runs.push([a[0] + ux * t0, a[1] + uz * t0, b[0], b[1]]);
  }
  }
  if (cfg.posts === 'spaced') runs.splice(0, runs.length, ...spacedRuns(runs, spacing)); // VIS-152
  let panel = 0;
  if (style === 'slanted' && !only) { // riukuaita: post pairs either side of the line, poles leaning along it between them
    const lean = THREE.MathUtils.degToRad(cfg.lean ?? 38), gap = cfg.poleGap ?? 0.28, Lp = (H + 0.35) / Math.sin(lean);
    for (const [ax, az, bx, bz] of runs) {
      const len = hypot(bx - ax, bz - az);
      if (len < 0.2) continue;
      const ux = (bx - ax) / len, uz = (bz - az) / len, steps = Math.max(1, Math.round(len / spacing));
      for (let s = 0; s <= steps; s++) for (const side of [-1, 1]) {
        const x = ax + (bx - ax) * (s / steps) - uz * side * 0.1, z = az + (bz - az) * (s / steps) + ux * side * 0.1;
        posts.push([x, heightAt(x, z), z, 0.82]);
      }
      const run = Lp * Math.cos(lean); // a pole's footprint along the line: the first poles start before the run so it is full
      for (let d = -run * 0.7, k = 0; d < len - run * 0.3; d += gap, k++) {
        const x0 = ax + ux * d, z0 = az + uz * d, l = Lp * (0.9 + ((k * 7) % 5) * 0.04), off = (k % 2 ? 0.035 : -0.035);
        const x1 = x0 + ux * l * Math.cos(lean), z1 = z0 + uz * l * Math.cos(lean);
        const g = new THREE.CylinderGeometry(0.03, 0.045, l, 5, 1, true);
        g.rotateZ(-(Math.PI / 2 - lean));
        g.rotateY(-atan2(uz, ux));
        const yb = heightAt(Math.min(Math.max(x0, Math.min(ax, bx)), Math.max(ax, bx)), Math.min(Math.max(z0, Math.min(az, bz)), Math.max(az, bz)));
        g.translate((x0 + x1) / 2 - uz * off, yb + (l * Math.sin(lean)) / 2 - 0.15, (z0 + z1) / 2 + ux * off);
        rails.push(g);
      }
    }
  }
  // WS-81 / VIS-188 (the owner's pick "Evenly spaced", 2026-10-22): the bulbs go evenly along the fence's whole length,
  // the leftover carried across posts, runs and lines (one gap = the runs' length / fenceBulbCount, ~bulbs.spacing): the
  // count the Fences panel says, on every fence (was a rounded number per post gap unless `bulbsAlong` / `bulbs.along`)
  const lit = cfg.bulbs && !cfg.bulbs.off, kind = (lit && fenceLightLook(cfg.bulbs).type) || 'bulbs', KL = FENCE_LIGHTS[kind]; // (fence light kinds)
  const even = lit && kind === 'bulbs', hung = kind === 'fairy' || kind === 'festoon'; // bulbs evenly along; a string from post to post
  const nB = even ? fenceBulbCount(lineLen, cfg.bulbs.spacing) : 0, gapB = nB ? runs.reduce((s, [ax, az, bx, bz]) => s + hypot(bx - ax, bz - az), 0) / nB : 0;
  let runAt = 0, kB = 0; // WS-81: the distance along the runs so far, the next bulb
  // (WS-89 follow-up) `bulbsSkip`: lines whose bulbs are left out (within 0.45 m: a stretch with lights of its own, built apart)
  const skip = Array.isArray(cfg.bulbsSkip) && cfg.bulbsSkip.length ? cfg.bulbsSkip : null;
  const skipped = (x, z) => skip.some((L) => L.some((p, i) => i && stretchDist(x, z, L[i - 1][0], L[i - 1][1], p[0], p[1]) < 0.45));
  // (fence light kinds) `bulbs`: what is drawn ([part, x, y, z, sx, sy, sz], lightsGeometry); `cast`: the points that
  // light the snow, in order along the fence (a bulb's centre; an icicle strand's middle; a post lamp's)
  const LP = lightParts(), cast = [], light = (q, x, y, z, sx, sy = sx, sz = sx, c = true) => { bulbs.push([q, x, y, z, sx, sy, sz]); if (c) cast.push([x, y, z]); };
  const bulbAt = (xa, ya, za, xb, yb, zb, t, ox, oz, top) => { const x = xa + (xb - xa) * t + ox, z = za + (zb - za) * t + oz; if (!skip || !skipped(x, z)) light(LP.ball, x, ya + (yb - ya) * t + top + 0.08, z, KL.r); };
  // WS-89: the bulbs sit on what the style has on top (fenceTop: the top rail, slanted: its post pairs' tops; was a fixed
  // H + 0.08 over every style, so a 1- or 2-rail fence floated them up to 0.83 m above it and slanted had none)
  const top = fenceTop(style, nR, H), panels = style !== 'slanted'; // (slanted: its posts and poles are drawn above)
  // a string from one post to the next (`span` m apart): evenly, lowest in the middle, a little out from the rails so it
  // hangs in front of them: sag x the span (`bulbs.sag`, else the kind's), clear of the ground; fairy lights with no sag
  // set as built (at most 0.1 m, along the rail)
  const sagSet = Number.isFinite(cfg.bulbs?.sag), free = sagSet || kind === 'festoon', dipOf = (span) => Math.max(0, Math.min((sagSet ? cfg.bulbs.sag : KL.sag) * span, free ? top + KL.lift - 0.35 : 0.1));
  const stringRun = (xa, ya, za, xb, yb, zb, ox, oz, span, nx, nz) => {
    const n = Math.max(1, Math.round(span / KL.gap)), dip = dipOf(span), out = free ? 0.07 : 0, low = (KL.hang || 0) + (kind === 'festoon' ? KL.r : 0);
    for (let k = 0; k < n; k++) { const t = (k + 0.5) / n, x = xa + (xb - xa) * t + ox + nx * out, z = za + (zb - za) * t + oz + nz * out; if (!skip || !skipped(x, z)) light(kind === 'fairy' ? LP.oct : LP.ball, x, ya + (yb - ya) * t + top + KL.lift - 4 * dip * t * (1 - t) - low, z, KL.r); }
  };
  // icicle strands of 1-4 bulbs under the top rail, their lengths varied along the run (as the boardwalk's railing)
  const icicleRun = (xa, ya, za, xb, yb, zb, ox, oz, span, nx, nz) => {
    const m = Math.max(1, Math.round(span / KL.gap)), drop = Math.min(KL.drop, top - 0.3), y0 = top - (sawn ? 0.08 : 0.06);
    for (let j = 0; j < m; j++) {
      const t = (j + 0.5) / m, x = xa + (xb - xa) * t + ox + nx * 0.06, z = za + (zb - za) * t + oz + nz * 0.06, y = ya + (yb - ya) * t + y0, n = 1 + ((panel * 5 + j * 7) % 4);
      if (skip && skipped(x, z)) continue;
      for (let s = 0; s < n; s++) light(LP.oct, x, y - (drop * s) / 4, z, KL.r, KL.r, KL.r, false);
      cast.push([x, y - (drop * (n - 1)) / 8, z]);
    }
  };
  // a lamp on top of a post, the next one `bulbs.spacing` m on at the soonest (every post, as built: one every 1.1 m)
  const capY = style === 'slanted' ? 0.82 * (H + 0.325) - 0.05 : H + (sawn || style === 'roundpole' ? 0.275 : 0.225), capW = sawn ? 0.12 : style === 'roundpole' ? 0.17 : 0.14;
  let lampAt = -Infinity, lampNext = 0;
  const postLamp = (x, z, d, pg) => {
    if (d - lampAt < 0.3 || d + pg / 2 < lampNext || (skip && skipped(x, z))) return;
    light(LP.box, x, heightAt(x, z) + capY + 0.06, z, capW, 0.12, capW);
    lampAt = d; lampNext = d + (cfg.bulbs.spacing ?? 1.1);
  };
  for (const [ax, az, bx, bz] of runs) {
    const len = hypot(bx - ax, bz - az);
    if (len < 0.2) { // too short for a panel; WS-81: its share of the bulbs stays (on the ground line + the rail's height)
      if (hung && len > 0.05) stringRun(ax, heightAt(ax, az), az, bx, heightAt(bx, bz), bz, 0, 0, len, 0, 0);
      for (; kB < nB && (kB + 0.5) * gapB < runAt + len; kB++) bulbAt(ax, heightAt(ax, az), az, bx, heightAt(bx, bz), bz, ((kB + 0.5) * gapB - runAt) / len, 0, 0, top);
      runAt += len;
      continue;
    }
    if (!panels && !lit) { runAt += len; continue; }
    const steps = Math.max(1, Math.round(len / spacing)), ux = (bx - ax) / len, uz = (bz - az) / len;
    for (let s = 0; s < steps; s++, panel++) {
      const t0 = s / steps, t1 = (s + 1) / steps;
      const xa = ax + (bx - ax) * t0, za = az + (bz - az) * t0, xb = ax + (bx - ax) * t1, zb = az + (bz - az) * t1;
      if (panels) posts.push([xa, heightAt(xa, za), za, 1]);
      if (panels && s === steps - 1) posts.push([xb, heightAt(xb, zb), zb, 1]);
      const ya = heightAt(xa, za), yb = heightAt(xb, zb), seg = hypot(len / steps, yb - ya);
      const off = !panels ? 0 : sawn ? 0.07 : style === 'roundpole' && panel % 2 ? 0.11 : 0; // panels alternate in and out (they overlap at the posts); WS-63: sawn boards on one face
      const ox = -uz * off, oz = ux * off, ext = sawn ? 0.02 : style === 'roundpole' ? 0.3 : 0;
      if (hung) stringRun(xa, ya, za, xb, yb, zb, ox, oz, len / steps, -uz, ux); // (fence light kinds) between these two posts
      else if (kind === 'icicle') icicleRun(xa, ya, za, xb, yb, zb, ox, oz, len / steps, -uz, ux);
      else if (kind === 'post' && lit) { postLamp(xa, za, runAt + (len * s) / steps, len / steps); if (s === steps - 1) postLamp(xb, zb, runAt + len, len / steps); }
      else if (even) { // WS-62: warm bulbs on the top rail (photo), every bulbs.spacing m, following the slope
        const d0 = runAt + (len * s) / steps, dl = len / steps;
        for (; kB < nB && (kB + 0.5) * gapB < d0 + dl; kB++) bulbAt(xa, ya, za, xb, yb, zb, ((kB + 0.5) * gapB - d0) / dl, ox, oz, top); // WS-81: those in this gap
      }
      for (let r = 0; panels && !only && r < nR; r++) {
        const h = railHeight(style, r, nR, H); // WS-89 (one function with the bulbs' fenceTop)
        const rad = style === 'roundpole' ? 0.05 + ((r * 7 + panel * 3) % 5) * 0.004 : 0.045;
        const g = sawn ? new THREE.BoxGeometry(0.12, seg + ext, 0.035) : new THREE.CylinderGeometry(rad, rad * 1.08, seg + ext, 5, 1, true); // WS-63: box x turns upright
        g.rotateZ(Math.PI / 2 + atan2(yb - ya, len / steps));
        g.rotateY(-atan2(zb - za, xb - xa));
        g.translate((xa + xb) / 2 + ox, (ya + yb) / 2 + h, (za + zb) / 2 + oz);
        rails.push(g);
      }
    }
    runAt += len; // WS-81
  }
  for (const g of gates) { // two taller posts + a closed plank gate across the opening
    if (!g.dir || g.draw === false) continue; // not on any fence edge; VIS-234: draw false = only the opening (a landmark GLB is the gate)
    const [gx, gz] = g.at, [ux, uz] = g.dir || [1, 0], w = g.width;
    const y = heightAt(gx, gz), ang = -atan2(uz, ux);
    for (const s of [1, -1]) posts.push([gx + ux * s * w / 2, heightAt(gx + ux * s * w / 2, gz + uz * s * w / 2), gz + uz * s * w / 2, 1.4]);
    for (let k = 0; k < 7; k++) { // vertical-ish planks
      const t = (k + 0.5) / 7 - 0.5;
      gateGeo.push(new THREE.BoxGeometry(0.035, H - 0.15, w / 7 - 0.03).rotateY(ang + Math.PI / 2).translate(gx + ux * t * w, y + H / 2 + 0.05, gz + uz * t * w));
    }
    for (const h of [0.35, H - 0.15]) gateGeo.push(new THREE.BoxGeometry(w - 0.1, 0.1, 0.05).rotateY(ang).translate(gx, y + h, gz));
    gateGeo.push(new THREE.BoxGeometry(hypot(w - 0.2, H - 0.6), 0.09, 0.05).rotateZ(atan2(H - 0.6, w - 0.2)).rotateY(ang).translate(gx, y + H / 2, gz));
    if (g.lights) { // bulbs on a sagging wire along the fence top either side of the gate
      for (let d = -g.lights; d <= g.lights; d += 1.1) {
        if (Math.abs(d) < w / 2) continue;
        const bx = gx + ux * d, bz = gz + uz * d, sag = 0.12 * Math.abs(Math.sin((d / 3.4) * Math.PI));
        light(LP.ball, bx, heightAt(bx, bz) + H + 0.35 - sag, bz, 0.07);
      }
    }
  }
  const group = new THREE.Group(); group.name = 'fence';
  // WS-63 materials: wood = cfg.material (a library key), else kelo for poles (weathered grey #6f665b unless a colour is
  // given, photos) / pine for rails. `color` tints a flat material only (a textured key keeps its own look); one material
  // per key + colour (was: the first fence's colour for every later fence).
  const pole = style === 'roundpole' || style === 'slanted', wk = [cfg.material, pole ? 'kelo' : 'pine'].find((k) => M[k]);
  const wc = !M[wk].map && (cfg.color || (pole && wk == 'kelo' && '#6f665b'));
  const wood = wc ? ((M._fw ||= {})[wk + wc] ||= Object.assign(M[wk].clone(), { color: new THREE.Color(wc) })) : M[wk];
  const postGeo = sawn ? new THREE.BoxGeometry(0.1, H + 0.35, 0.1) : style === 'roundpole' ? new THREE.CylinderGeometry(0.08, 0.1, H + 0.35, 6) : style === 'slanted' ? new THREE.CylinderGeometry(0.04, 0.05, H + 0.35, 5) : new THREE.CylinderGeometry(0.07, 0.09, H + 0.25, 5);
  // WS-75 perf: the posts are merged into the rails' mesh (same material): one render object per pass instead of two (on
  // WebGPU each object costs the processor ~20 us a pass); the fence is static and rebuilt whole when edited (fenceFor)
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), sc = new THREE.Vector3();
  const woodGeos = only ? [] : [...posts.map(([x, y, z, k]) => postGeo.clone().applyMatrix4(m4.compose(new THREE.Vector3(x, y + ((H + 0.3) * k) / 2 - 0.05, z), q, sc.set(1, k, 1)))), ...rails];
  if (woodGeos.length) { const rm = new THREE.Mesh(mergeGeometries(woodGeos), wood); rm.castShadow = true; group.add(rm); }
  if (gateGeo.length && !only) { const gm = new THREE.Mesh(mergeGeometries(gateGeo), M.woodGrey || M.wood); gm.castShadow = true; group.add(gm); }
  if (bulbs.length) {
    // WS-80 C: `bulbs.b` = their brightness (0-1; 0.6 = the lamp material's own), a lamp material of that gain on the night
    // ramp (materials.js setLightLevel); a fence of resort.fences (it has an `id`) marks them as its own (lazy/lights.js
    // leaves them out of the lights, Fine-tune sets them per fence) and light casting scales their lumens (`bulbLevel`)
    const lv = Number.isFinite(cfg.bulbs?.b) ? Math.max(0, cfg.bulbs.b) / 0.6 : 1, g = lv * (KL.glow ?? 1); // (fence light kinds: `glow`, a dense kind's bulbs dimmer)
    const bm = new THREE.Mesh(lightsGeometry(bulbs), g === 1 ? M.lamp : ((M._bulbs ||= {})[g] ||= (() => { const m = M.lamp.clone(); m.userData = { ...m.userData, baseKey: 'lamp', gain: g }; m.emissiveIntensity = M.lamp.emissiveIntensity * g; derivedMaterials.push(m); return m; })()));
    if (KL.glow) bm.userData.bulbGlow = KL.glow;
    if (cfg.id && lit) bm.userData.fence = cfg.id; // WS-89: + a paddock ring (main.js fenceFor: `paddock:<id>`); a fence lit only at its gates keeps them in Lights
    const share = KL.share; // (fence light kinds) what one of them casts, x a fence bulb's light
    if (share !== 1) bm.userData.bulbShare = share;
    if (lv * share !== 1) bm.userData.bulbLevel = lv * share;
    // WS-63 light: the points that light the snow in order along the fence (group frame): lazy/lightcast.js (lighting.cast.fences)
    bm.userData.bulbs = cast;
    group.add(bm);
  }
  return group;
}

/** (WS-89 follow-up) A fence's bulbs mesh (buildFence's, `userData.bulbs`) at brightness `b` (0-1; 0.6 = the lamp's own) at
 *  once, with no rebuild: its own copy of the lamp material (made once per mesh, on the night ramp as the shared ones:
 *  materials.js setLightLevel), and `bulbLevel` for light casting (it reads it on its next rescan). */
export function bulbsLevel(M, mesh, b) {
  if (!mesh?.material || !Number.isFinite(b)) return;
  const lv = Math.max(0, b) / 0.6, g = lv * (mesh.userData.bulbGlow ?? 1);
  if (!mesh.userData.ownLamp) { const m = M.lamp.clone(); m.userData = { ...m.userData, baseKey: 'lamp', gain: g }; derivedMaterials.push(m); mesh.material = m; mesh.userData.ownLamp = true; mesh.addEventListener('dispose', () => { const i = derivedMaterials.indexOf(m); if (i >= 0) derivedMaterials.splice(i, 1); m.dispose(); }); } // (lazy/fine.js unbuild sends `dispose`: the copy goes with the mesh)
  mesh.material.userData.gain = g;
  mesh.material.emissiveIntensity = M.lamp.emissiveIntensity * g;
  const k = lv * (mesh.userData.bulbShare ?? 1); // (fence light kinds: one light's share of a bulb's)
  if (k !== 1) mesh.userData.bulbLevel = k; else delete mesh.userData.bulbLevel;
}

/**
 * VIS-267 W7b: the built fences (buildFence groups: paddock rings, fence lines) as one draw per material. Each fence was
 * its own 1-3 draws (rails and posts, gate, bulbs); on a phone every draw costs fixed time. Meshes of one material and
 * the same draw state merge into one mesh, in world space, under ONE group named 'fence' (light casting reads every
 * mesh under a fence group as a thin caster, as before). A fence's own bulbs (userData.fence) merge too, at one
 * brightness each: `userData.bulbRuns` keeps each fence's string of bulbs apart ([{ pts, k }], world space), so light
 * casting lights them as before. Gate-only bulbs (no fence id) stay as they are (lazy/lights.js lists those as lights).
 * For the visitor's page only: the editor's frame rebuilds a touched fence (lazy/fine.js) and keeps them apart.
 * Returns the new group (null: nothing to merge); the merged meshes leave their old groups, which stay (empty) where
 * they were.
 */
export const JOIN_MAX_TRIS = 20000;
export function joinFences(groups, { bulbs = true } = {}) {
  const sets = new Map();
  for (const g of groups) {
    if (!g || !(g.name === 'fence' || g.name.startsWith('fence:')) || !g.visible) continue;
    g.updateMatrixWorld(true);
    for (const m of g.children) {
      if (!m.isMesh || m.isInstancedMesh || Array.isArray(m.material) || !m.visible || m.children.length || m.userData.stretches) continue; // (a stretch's own bulbs: Fine-tune's sets)
      const B = !!m.userData.bulbs;
      if (B && (!bulbs || !m.userData.fence || m.userData.ownLamp)) continue;
      if (!B && Object.keys(m.userData).length) continue;
      if ((m.geometry.index ? m.geometry.index.count : m.geometry.attributes.position.count) / 3 > JOIN_MAX_TRIS) continue; // a long fence keeps its own draw: out of view it is not drawn at all (Lumo's 2.9 km track fence, 145k triangles: joined, the top view drew +216k)
      const k = [m.material.uuid, B ? `b${m.userData.bulbLevel ?? 1}` : '', +m.castShadow, +m.receiveShadow, m.renderOrder, m.layers.mask, +m.frustumCulled, m.geometry.index ? 'i' : 'n', Object.keys(m.geometry.attributes).sort().join(',')].join('|');
      if (!sets.has(k)) sets.set(k, []);
      sets.get(k).push(m);
    }
  }
  const out = new THREE.Group(); out.name = 'fence';
  for (const list of sets.values()) {
    if (list.length < 2) continue;
    const geo = mergeGeometries(list.map((m) => m.geometry.clone().applyMatrix4(m.matrixWorld)));
    if (!geo) continue; // (attributes that do not merge: those meshes stay as they are)
    const a = list[0], mesh = new THREE.Mesh(geo, a.material);
    Object.assign(mesh, { castShadow: a.castShadow, receiveShadow: a.receiveShadow, renderOrder: a.renderOrder, frustumCulled: a.frustumCulled });
    mesh.layers.mask = a.layers.mask;
    if (a.userData.bulbs) {
      mesh.userData.fence = list.map((m) => m.userData.fence);
      if (a.userData.bulbLevel != null) mesh.userData.bulbLevel = a.userData.bulbLevel;
      const v = new THREE.Vector3();
      mesh.userData.bulbRuns = list.map((m) => ({ pts: m.userData.bulbs.map((p) => v.set(...p).applyMatrix4(m.matrixWorld).toArray()), k: m.userData.bulbLevel ?? 1 }));
    }
    for (const m of list) { m.removeFromParent(); m.geometry.dispose(); }
    out.add(mesh);
  }
  if (!out.children.length) return null;
  // in the scene where the first fence was: whatever walks the scene (light casting's bulb strings and thin casters)
  // meets the fences in the order it met them before, so the light map adds its lights in the same order
  const first = groups.find((g) => g?.parent && (g.name === 'fence' || g.name.startsWith('fence:')));
  if (first) { const P = first.parent; P.add(out); P.children.splice(P.children.indexOf(out), 1); P.children.splice(P.children.indexOf(first), 0, out); }
  return out;
}

/** Harness on the animal's bones (ride photos): patterned girth band, neck collar, halter. */
export function harnessAnimal(rig, { band = '#e39a2b', pattern = '#b8302a', collar = '#d9822b', halter = '#3a2a22' } = {}) {
  const bone = (n) => rig.object.getObjectByName(n);
  const neck = bone('neck1'), chest = bone('chest'), head = bone('head');
  const strap = (r, w, c1, c2, zig) => { // an open band (a short tube along the body axis), patterned round its girth
    const g = new THREE.CylinderGeometry(r, r, w, 28, 2, true).rotateZ(Math.PI / 2);
    const n = g.attributes.position.count, col = new Float32Array(n * 3), A = new THREE.Color(c1), B = new THREE.Color(c2);
    for (let i = 0; i < n; i++) {
      const a = atan2(g.attributes.position.getZ(i), g.attributes.position.getY(i)), x = g.attributes.position.getX(i) / w + 0.5;
      const c = zig && Math.abs(x - 0.5 - 0.3 * Math.sin(a * 9)) < 0.18 ? B : A;
      col.set([c.r, c.g, c.b], i * 3);
    }
    g.setAttribute('color', attr(col));
    return new THREE.Mesh(g, vertexColorMaterial());
  };
  if (chest) { const c = strap(0.25, 0.13, band, pattern, true); c.position.set(-0.1, -0.2, 0); c.scale.set(1, 1.28, 0.92); chest.add(c); }
  if (neck) { const c = strap(0.13, 0.07, collar, band, false); c.position.set(0.03, -0.02, 0); c.rotation.z = 0.9; c.scale.set(1, 1.15, 1); neck.add(c); }
  if (head) { const c = strap(0.075, 0.03, halter, halter, false); c.position.set(0.12, -0.04, 0); c.rotation.z = -0.5; c.scale.set(1, 1.2, 1); head.add(c); }
  rig.headBone = head;
  rig.chestBone = chest;
}

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _side = new THREE.Vector3();
/** Up to `count` ropes in one lit mesh (thin 3-sided tubes, rewritten whenever their movers are posed): begin(); set(a, b, colour, sag); end().
 *  VIS-267 step 3: end() keeps the geometry's bounding sphere round the ropes drawn (was computed once, by rest.js or the
 *  shadow keeper, round every slot: the unused ones sit at the map's origin, so a ride's ropes "met" every view of the
 *  village and woke the map, and a sledder's rope kept the movers' live shadow map redrawn); none drawn = a sphere far
 *  under the map that meets no view. True bounds also let the renderer cull them (and their shadow) out of view. */
export function createRopes(count) {
  const SEG = 6, pos = new Float32Array(count * (SEG + 1) * 9), nor = pos.slice(), col = pos.slice(), index = [];
  for (let r = 0; r < count; r++) for (let j = 0; j < SEG; j++) for (let k = 0; k < 3; k++) {
    const a = (r * (SEG + 1) + j) * 3 + k, b = a - k + ((k + 1) % 3);
    index.push(a, b, a + 3, b, b + 3, a + 3);
  }
  const geo = new THREE.BufferGeometry(), C = new THREE.Color();
  for (const [name, arr] of [['position', pos], ['normal', nor], ['color', col]]) geo.setAttribute(name, attr(arr));
  geo.setIndex(index);
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, -1e7, 0), 0); // (nothing drawn yet)
  const mesh = new THREE.Mesh(geo, vertexColorMaterial()); mesh.name = 'ride-ropes';
  let n = 0, x0 = 0, y0 = 0, z0 = 0, x1 = 0, y1 = 0, z1 = 0; // the drawn ropes' box
  return {
    mesh,
    begin() { n = 0; x0 = y0 = z0 = Infinity; x1 = y1 = z1 = -Infinity; },
    set(a, b, color, sag = 0, r = 0.013) {
      if (n >= count) return;
      const tx = b.x - a.x, ty = b.y - a.y, tz = b.z - a.z, l = hypot(tx, tz) || 1e-3, sx = -tz / l, sz = tx / l;
      const drop = sag * hypot(l, ty) * 4;
      x0 = Math.min(x0, a.x - r, b.x - r); x1 = Math.max(x1, a.x + r, b.x + r); z0 = Math.min(z0, a.z - r, b.z - r); z1 = Math.max(z1, a.z + r, b.z + r);
      y0 = Math.min(y0, Math.min(a.y, b.y) - drop / 4 - r); y1 = Math.max(y1, a.y + r, b.y + r); // (the sag hangs at most drop / 4 below the lower end)
      C.set(color);
      for (let j = 0, w = n * (SEG + 1) * 9; j <= SEG; j++) for (let k = 0; k < 3; k++, w += 3) {
        const u = j / SEG, ang = k * 2.0944 + 0.5236, cs = Math.cos(ang), ny = Math.sin(ang);
        nor[w] = sx * cs; nor[w + 1] = ny; nor[w + 2] = sz * cs;
        pos[w] = a.x + tx * u + nor[w] * r; pos[w + 1] = a.y + ty * u - drop * u * (1 - u) + ny * r; pos[w + 2] = a.z + tz * u + nor[w + 2] * r;
        col[w] = C.r; col[w + 1] = C.g; col[w + 2] = C.b;
      }
      n++;
    },
    end() {
      geo.setDrawRange(0, n * SEG * 18); mesh.visible = n > 0; for (const k of ['position', 'normal', 'color']) geo.attributes[k].needsUpdate = true; // WS-75: no draw of zero indices (a WebGPU warning)
      const s = (geo.boundingSphere ||= new THREE.Sphere()); // VIS-267 step 3: round the ropes drawn
      if (n) { s.center.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2); s.radius = hypot(x1 - x0, y1 - y0, z1 - z0) / 2; } else { s.center.set(0, -1e7, 0); s.radius = 0; }
    },
  };
}
/** One team: two taut traces (girth band -> sled front) and the slack lead rope (halter -> `lead`). */
export function teamRopes(ropes, rig, sled, lead) {
  const at = (b, v) => rig.bonePos?.(b, v) || (b || rig.object).getWorldPosition(v); // WS-72 step 3: bones posed on the GPU (animals.js)
  at(rig.chestBone, _a); _a.y -= 0.32;
  _side.set(0, 0, 0.24).applyQuaternion(rig.object.quaternion);
  for (const s of [1, -1]) {
    _a.addScaledVector(_side, s);
    ropes.set(_a, _b.set(0.9, 0.3, s * 0.27).applyMatrix4(sled.matrixWorld), TRACE);
    _a.addScaledVector(_side, -s);
  }
  if (!lead) return;
  at(rig.headBone, _a); _a.y -= 0.12;
  ropes.set(_a, lead, ROPE, 0.09);
}

// ---- VIS-267 step 3: the life pace's movers (lifepace.js: pose(m, x, y, z, r, h) = the s to pose mover m by now, 0 = not now)
/** The life pace's own choice: in view, near ones every frame, far ones on the shared tick. */
export const LIFE = (m, x, y, z, r, h) => lifePace.pose(m, x, y, z, r, h);
const FILL = 1e5; // m: a mover told to the life pace as filling the view (in view and near, wherever it is)
/** A pose the life pace does not choose (a model arriving, Film, the editor's place(), the ride the ride view is on),
 *  told to it as a mover filling the view: it keeps that spot as where the mover is drawn, and its clock (the seconds
 *  owed since its last pose are returned). */
export const FULL = (m, x, y, z) => lifePace.pose(m, x, y, z, FILL, FILL);
/** The sphere round a stretch of route T from arc length `a` back to `b`, `pad` m wider (the lanes kept right, a towed
 *  sled cutting a bend, the animals' width): its middle on the route and half its length (a chord is never longer than
 *  its arc). Writes `out` (T.at's x, y, z, h, k) and out.r. */
export function stretchBall(T, a, b, pad, out) { T.at((a + b) / 2, out); out.r = Math.abs(a - b) / 2 + pad; return out; }

// ---- WS-69 Life: the editing itself is lazy/fine.js (editor frame only); here only what the build needs too
/** The route a ride's package gives it: routes[route], else its own path. null = no usable route. */
export const packageRoute = (cfg) => {
  const r = cfg.routes?.[cfg.route] || (cfg.path ? { path: cfg.path, closed: cfg.closed, direction: cfg.direction } : null);
  return Array.isArray(r?.path) && r.path.length >= 2 ? r : null;
};
/** The ride's further rounds (resort.json rides[].rounds: [{ line, path, closed? }], each `path` = its line as built):
 *  the team drives its own route (`T`, sampled), then each round in turn, then its own again (Lumo's husky team: the
 *  3 km loop, then the 5 km route), switching while it waits at the start, so every round must pass `start`. A round
 *  leaves the start the way its own route does (driven against its drawing when it must): no turn on the spot there.
 *  Sampled as createRides drives them; [] for none. */
export function roundsOf(cfg, T, heightAt) {
  const st = cfg.start || [T.x[0], T.z[0]], h = T.at(T.nearest(st), {}).h;
  return (Array.isArray(cfg.rounds) ? cfg.rounds : []).filter((r) => Array.isArray(r?.path) && r.path.length >= 2).map((r) => {
    const drive = (path) => samplePath(driveLine(cfg, { path, closed: r.closed !== false }), heightAt), Q = drive(r.path);
    return Math.cos(Q.at(Q.nearest(st), {}).h - h) < 0 ? drive(r.path.slice().reverse()) : Q;
  });
}
/** WS-89: the line a ride drives on `route` ({ path, closed, direction }): a loop as it is; an open line out and back
 *  (the Life card's "out and back"), keeping right: `side` m right of the line out, a half turn round the far end, `side` m
 *  right of it back (its left going out), a half turn round the start, closed. Was: back at the start in one jump.
 *  `loop` (m; a vehicle that cannot turn on the spot: snowmobiles): the turn at the far end is a balloon of that radius
 *  beyond the line's end, entered from the right lane and left into the other; the start keeps its half turn (the yard
 *  they leave from: they come in slowly, and a balloon there would cross what stands round it).
 *  `turn` (horse rides): the half turns' radius when wider than `side` (a line of horses or a sleigh on shafts needs
 *  ~3 m, skill rides.md "a turn must be walkable by the whole line"): the two legs widen to it smoothly over the last
 *  2 x `turn` m before each end (a teardrop, no kink); without it the turn is as before.
 *  `meet` (m): the lanes are `side` apart only within `meet` m of each end (where a line of teams meets itself going
 *  out and coming back) and close onto the line itself over the next LANE_SHIFT m; elsewhere both legs drive its
 *  middle. Infinity = `side` all along.
 *  A loop with a tail (out along a stem, round a ring, back along the same stem: stemOf) is driven as an open line
 *  whose far turn is the ring (round it the way `direction` says): the legs keep right on the stem and the free end has
 *  the start's half turn (the owner 2026-10-31: on the stem's one line the snowmobiles turned on the spot at its end
 *  and drove through each other). */
export function rideRoute(route, side = 1.2, loop = 0, turn = side, meet = Infinity) {
  const S = route.closed !== false ? stemOf(route.path, route.direction) : null;
  if (route.closed !== false && !S) return route;
  let P = (S ? S.stem : route.path).filter((p, i, a) => !i || hypot(p[0] - a[i - 1][0], p[1] - a[i - 1][1]) > 1e-3);
  if (P.length < 2) return route;
  if (Number.isFinite(meet) || turn > side) P = P.flatMap((p, i) => { if (!i) return [p]; const [ax, az] = P[i - 1], k = Math.ceil(hypot(p[0] - ax, p[1] - az)); return Array.from({ length: k }, (_, j) => [ax + ((p[0] - ax) * (j + 1)) / k, az + ((p[1] - az) * (j + 1)) / k]); }); // (lanes that change along the line: a point a metre, else a sparse way's lane would blend over its whole stretch)
  const n = P.length, dir = (i) => { const [ax, az] = P[Math.max(0, i - 1)], [bx, bz] = P[Math.min(n - 1, i + 1)], l = hypot(bx - ax, bz - az) || 1; return [(bx - ax) / l, (bz - az) / l]; };
  const r = Math.max(side, Number.isFinite(turn) ? turn : side), cum = [0];
  for (let i = 1; i < n; i++) cum.push(cum[i - 1] + hypot(P[i][0] - P[i - 1][0], P[i][1] - P[i - 1][1]));
  const ease = (u) => u * u * (3 - 2 * u);
  const wide = (i) => {
    const d = Math.min(cum[i], cum[n - 1] - cum[i] + (S ? S.len / 2 : 0)), lane = d <= meet ? side : side * ease(Math.max(0, 1 - (d - meet) / LANE_SHIFT)); // (lanes apart only near the ends; a stem's far end: where the ring brings it back to itself, half round it)
    const e = S ? cum[i] : d; // (the turn: a stem's at its free end only, the ring is its far one)
    return r <= lane ? lane : lane + (r - lane) * ease(Math.max(0, 1 - e / (2 * r))); // (the legs open to the turn's radius near each end)
  };
  const at = (i, s) => { const [dx, dz] = dir(i), w = wide(i); return [P[i][0] - dz * w * s, P[i][1] + dx * w * s]; }; // right of travel = (-dz, dx)
  const balloon = (i) => { // round a circle of radius `loop` whose middle is 1.1 x `loop` beyond the end
    const [dx, dz] = dir(i), cx = P[i][0] + dx * loop * 1.1, cz = P[i][1] + dz * loop * 1.1;
    return Array.from({ length: 13 }, (_, k) => { const a = ((-150 + 25 * k) * Math.PI) / 180, f = Math.cos(a) * loop, rr = -Math.sin(a) * loop; return [cx + dx * f - dz * rr, cz + dz * f + dx * rr]; }); // -150 deg: behind the middle on the right of the way in, +150: on its left
  };
  const m = r > side ? 11 : 5, half = (i, back) => (loop > side && !back ? balloon(i) : Array.from({ length: m }, (_, k) => { const a = ((k + 1) * Math.PI) / (m + 1), [dx, dz] = dir(i), w = wide(i), c = Math.cos(a) * (back ? -1 : 1), f = Math.sin(a) * w * (back ? -1 : 1); return [P[i][0] - dz * w * c + dx * f, P[i][1] + dx * w * c + dz * f]; }));
  const out = P.map((_, i) => at(i, 1)), home = P.map((_, i) => at(i, -1)).reverse();
  const { direction: _sense, ...rest } = route; // (a sense of turning means nothing on a line driven both ways)
  return { ...rest, path: [...out, ...(S ? S.ring.slice(1, -1) : half(n - 1, false)), ...home, ...half(0, true)], closed: true };
}
/** (a loop with a tail, lines/measure.js tailLoop: pathrides.js lineOf's points) A closed path that turns back on
 *  itself at one point and runs back over the same points to where it left a ring: { stem: [the free end ... where the
 *  ring starts], ring: [that point, round, that point] (turned to `direction`, 'cw' | 'ccw', as samplePath does), len:
 *  the ring's length (m) }, else null. */
function stemOf(path, direction) {
  const n = path?.length || 0, p = (i) => path[((i % n) + n) % n], same = (a, b) => hypot(a[0] - b[0], a[1] - b[1]) < 0.05;
  if (n < 6) return null;
  for (let i = 0; i < n; i++) {
    if (!same(p(i - 1), p(i + 1))) continue;
    let k = 1;
    while (2 * k < n - 4 && same(p(i - k - 1), p(i + k + 1))) k++;
    const stem = Array.from({ length: k + 1 }, (_, j) => p(i + j));
    let ring = Array.from({ length: n - 2 * k + 1 }, (_, j) => p(i + k + j));
    if (ring.length < 4) return null;
    if (direction === 'cw' || direction === 'ccw') { let ar = 0; for (let j = 0; j < ring.length - 1; j++) ar += ring[j][0] * -ring[j + 1][1] - ring[j + 1][0] * -ring[j][1]; if ((ar > 0) !== (direction === 'ccw')) ring = ring.slice().reverse(); }
    let len = 0; for (let j = 1; j < ring.length; j++) len += hypot(ring[j][0] - ring[j - 1][0], ring[j][1] - ring[j - 1][1]);
    return { stem, ring, len };
  }
  return null;
}
/** Where a ride waits between rounds on its driven route T (m along it): `cfg.start` (its nearest point), else where the
 *  route starts; a line driven out and back (`open`: rideRoute) without a `start` waits its team's length `span` m on, so
 *  the whole team stands in line on the way out, not curled round the half turn behind the start (the owner 2026-10-31:
 *  a horseback line waited "in a cluster on top of each other" round its turn). */
export const startOn = (cfg, T, open, span) => (cfg.start ? T.nearest(cfg.start) : open ? Math.min(Math.max(0, span), T.len / 4) : 0);
/** Ride R's length behind R.s (m): a lazy module's `span` once it is in, else the give way's. */
export const spanOf = (R) => R.team?.span ?? R.gw?.back ?? 0;
/** The width (m) of the ride's packed-snow lane: its first trail's, its `trail.width`, else 2.6. */
export const trailWidth = (cfg) => cfg.trails?.[0]?.width ?? cfg.trail?.width ?? 2.6;
/** m over which a ride's two lanes close onto the line's middle (rideRoute `meet`). */
const LANE_SHIFT = 8;
/** The tightest half turn round an open line's end (m): skill rides.md "turning radius >= ~2 m" (the old 1.2 m lane's
 *  half turn came out at ~0.8 m once rounded, under tests/qa/ride.js's 1.5 m). */
const MIN_TURN = 2;
/** The line a ride drives on its route (samplePath's input; lazy/fine.js rebuilds an edited route with it too). Owner
 *  2026-10-24: rides follow the MIDDLE of the drawn line (he draws it clear of trees and fences); at 1.2-1.4 m right of
 *  it they drove along its edge and through a fence beside it. An open line is driven out and back on its middle; only
 *  where a line of several teams meets itself (half its length from each end, + 4 m) do the legs keep right, by a
 *  quarter of the line's width (`cfg.width`, else its trail's: the middle of each half) and at most `teams.side`.
 *  One team (a sleigh, a dog sled, a line of horses) never meets another: the middle all the way, the ends' turn only. */
export function driveLine(cfg, route) {
  const tm = cfg.teams || {}, n = tm.kind === 'horseback' ? tm.horses ?? 4 : tm.count ?? 3;
  const side = n > 1 ? Math.min(tm.side ?? 1.2, (cfg.width ?? trailWidth(cfg)) / 4) : 0, meet = n > 1 ? ((n - 1) * (tm.spacing ?? 6.8)) / 2 + 4 : 0;
  return { ...rideRoute(route, side, tm.loop ?? 0, Math.max(tm.turn ?? 0, MIN_TURN), meet), ...(tm.loop ? { smooth: Math.max(route.smooth ?? 1.2, 2.5) } : {}) }; // (a vehicle's line a little rounder)
}

// ---- WS-85: the kinds of ride, and a ride on a drawn line
const SLEIGH = { count: 3, speed: 1.2, spacing: 6.8, dwell: 30, guide: true, sled: { lights: false, riders: [2, 1, 2] } }; // the first client's live reindeer raito's teams
/** The team kinds lazy/horses.js draws (a GLB sleigh or saddles on tacked horses, the map's people). */
export const HORSE_TEAMS = ['horsesleigh', 'horseback'];
/** The kinds a path can carry: { label, teams, seasons? } (teams = the defaults of resort.rides[].teams; seasons: the
 *  kind's own default, else winter). */
export const RIDE_KINDS = {
  reindeer: { label: 'Reindeer sleigh ride', teams: { ...SLEIGH, animal: 'reindeer' } },
  husky: { label: 'Husky ride', teams: { kind: 'dogsled', animal: 'husky', count: 1, dogs: 4, speed: 3.4, dwell: 30, guests: 1 } }, // the second client's live husky team
  // horse sleigh (the owner, 2026-10-24): one horse in the shafts of a GLB sleigh, a driver and two guests (lazy/horses.js)
  horse: { label: 'Horse sleigh ride', teams: { kind: 'horsesleigh', animal: 'horse', count: 1, spacing: 9, speed: 1.2, trot: 3.6, dwell: 30, guests: 2, turn: 3.5 } }, // turn: a horse in shafts (30 deg against them, 2.9 m shafts) turns round an open line's end at ~3.5 m; trot: ~13 km/h on a long straight (the owner's trot, 2026-10-25; under the canter's 4.6 m/s)
  // horseback (the owner, 2026-10-24): a line in single file, the guide first; 1.65 m horses ~4 m nose to nose at a walk;
  // every season (horses are ridden all year: no reason in the data to keep them to winter)
  horseback: { label: 'Horseback ride', seasons: ['winter', 'spring', 'summer', 'autumn'], teams: { kind: 'horseback', animal: 'horse', count: 1, horses: 4, spacing: 4, speed: 1.3, trot: 3.3, dwell: 30, turn: 3 } }, // turn: the line rounds an open line's end at 3 m (at 1.2 m the four horses bunched up); trot: a guests' line a little slower than the sleigh
  // a guided tour, one guest driving each snowmobile (lazy/snowmobile.js), a convoy: ~23 km/h on the straight, slower in the turns, 15 s at the start (owner 2026-10-24)
  snowmobile: { label: 'Snowmobile tour', teams: { kind: 'snowmobile', count: 3, speed: 6.5, spacing: 20, dwell: 15, accel: 1.4, brake: 1.2, lateral: 1.8, side: 1.4, loop: 4.5, turn: 3 } }, // turn: the half turn in the yard they leave from (its line rounded by 2.5 m: at 2 m it came out under 1 m)
};
/** A whole resort.rides[] entry on a drawn line `pts` ([[x, z], ...]): its own place (= id), no trail of its own (the
 *  path is drawn already), the kind's teams with `speed` / `dwell` / `count` over them, the kind's seasons (winter for a
 *  sled ride) unless `seasons` says. `width`: the drawn line's width (m), kept as the entry's `width` (driveLine's lanes stay inside it). */
export function rideFromPath({ id, kind, pts, closed, name, speed, dwell, direction, seasons, count, width }) {
  const K = RIDE_KINDS[kind] || RIDE_KINDS.reindeer, teams = JSON.parse(JSON.stringify(K.teams));
  if (speed > 0) teams.speed = speed;
  if (dwell >= 0) teams.dwell = dwell;
  if (count >= 1) { if (teams.kind === 'horseback') teams.horses = Math.min(8, Math.round(count)); else teams.count = Math.round(count); } // how many go (sleighs, sleds, snowmobiles; on horseback the horses in the line, at most 8)
  return { id, place: id, name: name || K.label, kind: RIDE_KINDS[kind] ? kind : 'reindeer', path: pts.map(([x, z]) => [x, z]), closed: !!closed, ...(direction && { direction }), ...(width > 0 && { width }), trails: [], teams, seasons: seasons || [...(K.seasons || ['winter'])] };
}

// ---- give way (giveway.js; skill rides.md "Give way"): per kind of team, its rank (people 5 and loose animals
// 4 above every ride: who waits when two are in each other's way), `half` = its half width (m), the radius (m) its
// sleds and animals take on the board; `head` = m from R.s to its front (a lazy module's own `head` / `span` win: its
// layout is its own); `motor`: an engine, it claims its way (claims()). STAND = m kept clear between any ride's front and
// a mover it waits for; BERTH (giveway.js) between an animal and an engine, either way.
export { STAND };
export const GIVE = {
  sleigh: { rank: RANK.sleigh, half: 0.55, sled: 1.1, animal: 0.7, head: 0.9 },
  horsesleigh: { rank: RANK.horse, half: 0.7, sled: 1.5, animal: 0.9, head: 1 },
  horseback: { rank: RANK.horse, half: 0.45, sled: 0, animal: 0.9, head: 1 }, // (its `sled` is the view's seat frame, not a body)
  dogsled: { rank: RANK.dogs, half: 0.55, sled: 1.2, animal: 0.45, head: 0.5 },
  snowmobile: { rank: RANK.snowmobile, half: 0.6, sled: 1.5, animal: 0, head: 1.6, motor: true }, // (half: the GLB's plan, 1.2 m wide)
};
const HOLD = 1.2; // s the lane must stay clear before a ride that stood for someone sets off again (no stutter)
const CLEAR = 3; // s instead, once it stood for the berth (an animal ride for an engine, or one for animals): they are well off first
// claims (claims()): points every CLAIM m along a ride's route, TAIL m behind its last and, for an engine, LEAD s of its
// cruising speed (+ 4 m) ahead of its first: an animal ride stands back before an engine comes and until its last is a way off,
// and an engine waits until an animal ride's last is a way off
const CLAIM = 5, TAIL = 12, LEAD = 15;
const KEEP = 0.15; // m sideways per m driven while keeping right (~9 deg: a lane change over a few metres, never a slide on the spot)
const PASS = 1.5; // m/s at most while passing someone just beside its lane, or a ride waiting for it (giveway.js B.pass)
/** A pose on the route `o` (samplePath at's out) moved `kr` m to the right of travel (heading kept). */
export const keepRight = (o, kr) => { if (kr) { o.x += Math.sin(o.h) * kr; o.z += Math.cos(o.h) * kr; } return o; };
/**
 * Give way: how fast (m/s) a part of ride R spanning sBack..sHead along its route, going `v` m/s, may go now so it
 * stops STAND m short of the nearest mover it must give way to, BERTH m between an animal and an engine (the speed it
 * can still brake from: sqrt(2 brake gap)); Infinity = nothing in the way. `M` keeps that part's state: `hold` (once it
 * stood for someone it waits until the lane has been clear HOLD s, CLEAR s after the berth), `kr` (m kept right while any other mover is in its lane ahead, up to the ride's
 * `side`, eased by the metres driven), `gap` (m). R itself for the line (R.gw), each snowmobile of a convoy its own.
 */
export function giveWay(R, M, sBack, sHead, v, dt) {
  const G = R.gw, gap = movers.gap(R.T, sBack, sHead, BERTH + (v * v) / (2 * R.brake) + v + 2 + G.side / KEEP, G.half, M.kr, G.rank, G.id); // (looks ahead the berth + its braking distance + a second + 2 m + the metres keeping right takes, so it is over before it stops)
  if (gap < Infinity && movers.who) movers.by[G.id] = movers.who; // (the ride it waits for: a convoy's other snowmobile standing for a person does not hide it)
  const step = Math.max(0, v) * dt * KEEP, want = movers.near ? G.side : 0;
  M.kr += Math.max(-step, Math.min(step, want - M.kr));
  M.hold = v < 0.05 && gap < STAND + 1 ? (movers.wide ? CLEAR : HOLD) : Math.max(0, M.hold - dt);
  M.gap = movers.wide ? gap + BERTH - STAND : gap; // (the metres to it: the firmer brake only for one really close, not for the berth)
  return M.hold > 0 ? 0 : Math.min(Math.sqrt(2 * R.brake * Math.max(0, gap - STAND)), PASS + Math.sqrt(2 * R.brake * Math.max(0, movers.pass - 1))); // (slows to PASS by the time it is level with someone just beside it)
}

// ---- the ride
export function createRides({ scene, M, heightAt, rides = [], animalModels = {}, base = '', instancing = null, dogsled = null, folk = null, snowmobile = null, horses = null, tethered = null, props = null }) { // WS-34: instancing.js (optional); husky ride: dogsled / folk = main.js's fetches of lazy/dogsled.js and lazy/folk.js (folk: also for a sleigh line's herder, VIS-53, and horse rides' people); snowmobile: its fetch of lazy/snowmobile.js; horse rides and tied horses: horses = its fetch of lazy/horses.js (with folk), tethered = resort.tethered, props = resort.props (their frames)
  const live = [];
  let tied = null, SEASON = null, HIDDEN = null; // tied horses (lazy/horses.js createTethered), once it has arrived; the season and hidden places it is told on arrival
  // WS-85: a kind the map has no model of = the engine's copy; animalModels null = the map shows no animals (features.animals false)
  const animalsOn = !!animalModels; // (tied horses: none on a map without animals)
  animalModels = animalModels ? { ...Object.fromEntries(['reindeer', 'husky', 'horse'].map((k) => [k, { url: new URL(`../models/animals/${k}.glb`, import.meta.url).href }])), ...animalModels } : {};
  for (const cfg of rides) {
    const route = packageRoute(cfg);
    if (!route) continue;
    const tm = cfg.teams || {};
    const T = samplePath(driveLine(cfg, route), heightAt); // WS-89: an open line out and back (rideRoute) on its middle
    const objs = []; // WS-41: everything this ride draws (hidden out of season)
    const trails = cfg.trails || (cfg.path ? [{ path: cfg.path, closed: cfg.closed, width: trailWidth(cfg) }] : []);
    const count = tm.count ?? 3, speed = tm.speed ?? 1.2, spacing = tm.spacing ?? 6.8, dwell = tm.dwell ?? 25;
    const sStart = cfg.start ? T.nearest(cfg.start) : 0;
    const sledCfg = tm.sled || {};
    const riders = sledCfg.riders || [2, 1, 2, 2, 1];
    const sleds = [];
    const lazyTeam = { dogsled: dogsled && [dogsled, 'createDogTeams'], snowmobile: snowmobile && [snowmobile, 'createSnowmobiles'], horsesleigh: horses && [horses, 'createHorseTeams'], horseback: horses && [horses, 'createHorseTeams'] }[tm.kind] || null; // husky ride: dog teams pulling a basket sled; snowmobile tour; horse sleigh and horseback ride (lazy modules, fetched by main.js only for such a ride)
    const isDogs = !!lazyTeam; // (its teams are the module's: no sleighs, guide or draught animals here)
    for (let i = 0; i < (isDogs ? 0 : count); i++) {
      const { geometry, lights, guests, seat } = sledGeometry({ riders: riders[i % riders.length], seed: i * 2 + (cfg.id?.length || 0), apart: !i });
      const mesh = new THREE.Mesh(geometry, vertexColorMaterial()); mesh.castShadow = true;
      for (const g of guests) { const m = new THREE.Mesh(g, mesh.material); m.castShadow = true; m.name = 'guest'; mesh.add(m); } // WS-85: the first sled's guests apart (the ride view hides the one it sits in)
      if (seat) mesh.userData.sled = seat;
      if (sledCfg.lights) mesh.add(new THREE.Mesh(mergeGeometries(lights.map((p) => new THREE.IcosahedronGeometry(0.028, 0).translate(...p))), M.led));
      scene.add(mesh); sleds.push(mesh); objs.push(mesh);
      instancing?.addMeshes(mesh); // WS-34: one batched draw
    }
    for (const [px, pz, rot = 0] of isDogs ? [] : cfg.parked || []) { // empty sleds waiting at the start
      const { geometry } = sledGeometry({ riders: 0, blanket: false });
      const mesh = new THREE.Mesh(geometry, vertexColorMaterial()); mesh.castShadow = true;
      mesh.position.set(px, heightAt(px, pz), pz); mesh.rotation.y = THREE.MathUtils.degToRad(rot);
      scene.add(mesh); objs.push(mesh);
      instancing?.addMeshes(mesh);
    }
    const ropes = createRopes(tm.kind === 'snowmobile' ? 0 : tm.kind === 'dogsled' ? count * Math.max(2, tm.dogs ?? 4) * 3 : isDogs ? count * Math.max(1, tm.horses ?? 1) * 10 : count * 3); // (a horse: 2 reins of up to 5 spans)
    if (tm.kind !== 'snowmobile') { scene.add(ropes.mesh); objs.push(ropes.mesh); } // (snowmobiles pull nothing)
    const R = { cfg, T, sleds, guide: null, ropes, tow: [], animals: [], s: sStart, v: 0, wait: dwell * 0.4, speed, spacing, dwell, sStart, travelled: 0, count, objs, active: true, rear: tm.rear ?? 0.75, towLen: tm.tow ?? 2.1, // WS-85: rear, towLen (a horse is longer)
      accel: tm.accel ?? 0.5, brake: tm.brake ?? 0.35, lateral: tm.lateral ?? 0, // m/s2: speeding up, braking for the stop, (a vehicle) across in a turn
      trot: tm.trot ?? 0, pace: null, // m/s on a long straight (paceProfile; 0 = `speed` all the way), its profile for this route and speed
      at: [], gAt: null, ball: {}, // VIS-267 step 3: the animals' and the herder's spots by the logic (step), the line's sphere (the life pace's mover)
      pkg: { route, trails }, trailObjs: [], // WS-69 Life: the package's route and trails (Reset); its trail meshes apart (a new route rebuilds them)
      own: T, rounds: roundsOf(cfg, T, heightAt), lap: 0 }; // its own route and its further rounds, taken in turn (roundsOf; lap 0 = its own)
    for (const tr of trails) if ((tr.width ?? 2.6) > 0 && tr.path?.length > 1) { const m = trailMesh(samplePath(tr, heightAt), tr.width ?? 2.6, heightAt); scene.add(m); objs.push(m); R.trailObjs.push(m); }
    if (tm.guide !== false && !isDogs) R.guide = createHerder(folk, scene, instancing, R); // VIS-53: the herder (lazy/folk.js)
    // give way: its rank and stop, its own id on the board, how far right it may keep (a quarter of the line's
    // width, as driveLine's lanes), its length behind R.s (a lazy module's `span` once it is in) and front ahead of it
    const K = GIVE[tm.kind] || GIVE.sleigh;
    R.gw = { ...K, id: movers.id(), side: Math.min(tm.side ?? 1.2, (cfg.width ?? trailWidth(cfg)) / 4), back: isDogs ? 1.6 : (count - 1) * spacing + R.rear + R.towLen + 1.2, head: R.guide ? 2.6 : K.head, hold: 0, kr: 0, gap: Infinity, T: null, box: [0, 0, 0, 0] };
    R.open = route.closed === false; R.s = R.sStart = startOn(cfg, T, R.open, R.gw.back); // (an open line: the team waits in line on the way out)
    live.push(R);
    if (isDogs) { // husky ride, snowmobile tour, horse rides: the teams arrive by themselves; until then nothing of them is drawn
      R.noAnimals = true; R.team = { place() {} }; // R.team: the lazy module's handle (teams, place, dispose; lazy/horses.js also `view`)
      const [js, make] = lazyTeam;
      js.then((X) => {
        if (R.removed) return;
        R.team = X[make]({ scene, heightAt, instancing, base, animalModels, ropes, folk, M }, cfg, R);
        const was = R.sStart; R.sStart = startOn(cfg, R.T, R.open && !R.lap, spanOf(R)); if (R.s === was && !R.travelled) R.s = R.sStart; // (its own length now known: still waiting, it waits there)
        place(R);
      }).catch((err) => console.warn(`${tm.kind} ride failed:`, err));
      continue;
    }
    // the rigged model of animalModels[kind] (async); the sleds wait until it loads
    const kind = tm.animal || 'reindeer', model = animalModels[kind];
    if (model?.url) {
      const url = /^(https?:|\/)/.test(model.url) ? model.url : base + model.url;
      import('./animals.js').then(async (A) => {
        const mdl = await A.loadAnimalModel(url);
        if (R.removed) return; // WS-89: the ride was taken away while its model loaded (lazy/fine.js sets `removed`): no ghost animals
        const roles = ['cow', 'cow', 'young', 'cow', 'cow', 'young']; // draught animals: no rut-bull antlers
        for (let i = 0; i < count; i++) {
          let rand = (i + 1) * 7919 % 1000 / 1000; const rr = () => (rand = (rand * 9301 + 49297) % 233280 / 233280);
          const v = A.herdVariation(mdl.meta, roles[i % roles.length], rr);
          v.scale = Math.max(0.95, v.scale);
          const rig = A.createAnimal(mdl, { ...v, mode: 'idle', rand: rr });
          harnessAnimal(rig, tm.harness || {});
          scene.add(rig.object);
          instancing?.addRig(rig); // WS-34: instanced with the herd
          R.animals.push(rig); objs.push(rig.object); rig.object.visible = R.active;
        }
        if (R.active) place(R); // VIS-267 step 3: on the line at once (out of view it is posed no more until it is seen; show() places a ride that comes back)
      }).catch((err) => { R.noAnimals = true; console.warn('ride animal model failed:', err); degraded('ride-animals', err); });
    } else R.noAnimals = true; // no rigged model: the sleds still run
  }

  // tied horses (resort.tethered): standing at their rails, drawn and posed by lazy/horses.js; off without animals
  if (horses && animalsOn && Array.isArray(tethered) && tethered.length) horses.then((H) => { tied = H.createTethered({ scene, heightAt, instancing, base, animalModels, props }, tethered); if (SEASON) tied.setSeason(SEASON); if (HIDDEN) tied.setOff(HIDDEN); }).catch((err) => { console.warn('tied horses failed:', err); degraded('animals', err); });

  /** The ride's walk, or its trot on a long straight (its profile made again for a new route or speed, lazy/fine.js). */
  function paceAt(R) {
    if (!(R.trot > R.speed)) return R.speed;
    if (R.pace?.T !== R.T || R.pace.walk !== R.speed) {
      R.pace = { T: R.T, walk: R.speed, v: paceProfile(R.T, { walk: R.speed, trot: R.trot, brake: R.brake, sStart: R.sStart }) };
    }
    const { N, step, len, closed } = R.T, u = Math.round((closed ? ((R.s % len) + len) % len : R.s) / step);
    return R.pace.v[closed ? u % N : Math.max(0, Math.min(N - 1, u))];
  }
  const P = {}, tmp2 = new THREE.Vector3(); // animal -> hindquarters (R.rear) -> sled middle (R.towLen) (m)
  /** Every frame (main.js). VIS-267 step 3: `viewed` = the id of the ride the ride view is on (lazy/rideview.js active):
   *  posed every frame wherever it is (the camera rides it, or glides to it; also a QA driver measuring one ride). */
  function update(dt, viewed = null) {
    tied?.update(dt);
    const gw = movers.on && dt > 0; // give way: the board people and herds wrote this frame (main.js runs them first)
    if (gw) { // every ride's teams, so rides of different kinds see each other; then, with an engine and an animal ride both out, their claims (bodies first: a full board drops claims)
      let kinds = 0; // (1: an engine, 2: an animal ride)
      for (let i = 0; i < live.length; i++) if (live[i].active && !live[i].film) { bodies(live[i]); kinds |= live[i].gw.motor ? 1 : 2; }
      if (kinds > 2) for (let i = 0; i < live.length; i++) if (live[i].active && !live[i].film) claims(live[i]);
    }
    for (const R of live) {
      if (!R.active) { away(R); continue; } // WS-41: out of season
      if (R.film) { place(R, dt); continue; } // VIS-265: Film holds it (film)
      if (!R.noAnimals && R.animals.length < R.count) continue; // animals still loading (the sleds wait at the start: placed when made)
      if (gw) movers.by[R.gw.id] = 0;
      // dwell at the start, speed up to a walk, slow to a stop there after one round
      if (R.wait > 0) { R.wait -= dt; R.v = 0; if (R.wait <= 0) R.travelled = 0; }
      else {
        const left = R.T.len - R.travelled, vStop = Math.sqrt(Math.max(0, 2 * R.brake * Math.max(0, left - 0.3)));
        let vTurn = paceAt(R) / (1 + 0.9 * R.T.at(R.s + 1, P).k); // slower in a tight turn
        if (R.lateral > 0) for (const d of [1, 4, 10, 18, 28]) { // a vehicle: at most `lateral` m/s2 across in each turn ahead it can still brake for
          if (d > left) break;
          vTurn = Math.min(vTurn, Math.sqrt(R.lateral / Math.max(1e-4, R.T.at(R.s + d, P).k) + 2 * R.brake * Math.max(0, d - 1)));
        }
        let dec = Math.max(0.6, R.brake * 1.5); // m/s2
        if (gw) { // give way: no faster than it can still stop short of the mover ahead; a firmer brake when one steps in close
          const G = R.gw;
          vTurn = Math.min(vTurn, giveWay(R, G, R.s - (R.team?.span ?? G.back), R.s + (R.team?.head ?? G.head), R.v, dt));
          if (G.gap < Infinity) dec = Math.max(dec, Math.min(6, (R.v * R.v) / (2 * Math.max(0.3, G.gap - 0.6))));
        }
        R.v = Math.min(vStop + 0.05, R.v + Math.max(-dt * dec, Math.min(dt * R.accel, vTurn - R.v)));
        const d = Math.min(R.v * dt, left);
        R.s += d; R.travelled += d;
        if (left - d <= 0.01) { R.wait = R.dwell; R.v = 0; if (R.rounds.length) toRound(R, R.lap + 1); R.s = R.sStart; if (!R.T.closed) R.tow = []; }
      }
      R.viewDt = dt;
      move(R, dt, R.cfg.id === viewed ? (R.viewPace ||= (m, x, y, z) => FULL(m, x, y, z) || R.viewDt) : LIFE); // (every team of it; `|| dt`: a driver that steps the rides alone, tests/qa/ride.js update(dt, id), owes no time)
    }
    // the next frame's board: written only while a ride runs, and only within the box of the running rides' lanes
    let on = false, x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let i = 0; i < live.length; i++) {
      const R = live[i], G = R.gw;
      if (!R.active || !G) continue;
      if (G.T !== R.T) { // (a new route, lazy/fine.js: its box again)
        const b = G.box; b[0] = b[1] = Infinity; b[2] = b[3] = -Infinity;
        for (let k = 0; k < R.T.N; k++) { const x = R.T.x[k], z = R.T.z[k]; if (x < b[0]) b[0] = x; if (z < b[1]) b[1] = z; if (x > b[2]) b[2] = x; if (z > b[3]) b[3] = z; }
        G.T = R.T;
      }
      on = true; x0 = Math.min(x0, G.box[0]); z0 = Math.min(z0, G.box[1]); x1 = Math.max(x1, G.box[2]); z1 = Math.max(z1, G.box[3]);
    }
    movers.clear(); movers.on = on; movers.reach(x0 - 6, z0 - 6, x1 + 6, z1 + 6);
  }
  /** Give way: ride R's people, animals and sleds onto the board (where its logic had them last: VIS-267 step 3, a ride
   *  out of view is not posed, its objects stay where they were last drawn), as its own (its id). */
  function bodies(R) {
    const G = R.gw, k = G.rank, id = G.id;
    if (R.team) {
      const ts = R.team.teams;
      for (let i = 0; ts && i < ts.length; i++) {
        const T = ts[i], dg = T.dogs || T.horses;
        if (T.sled && G.sled) { const p = T.spot || T.sled.position; movers.add(p.x, p.z, G.sled, k, id); } // (spot: the module's logic, lazy/dogsled.js, horses.js, snowmobile.js)
        for (let j = 0; dg && j < dg.length; j++) { const p = dg[j].spot || dg[j].rig.object.position; movers.add(p.x, p.z, G.animal, k, id); }
      }
      return;
    }
    if (R.guide) { const p = R.gAt || R.guide.g.position; movers.add(p.x, p.z, 0.35, k, id); }
    for (let i = 0; i < R.sleds.length; i++) { const p = R.tow[i] || R.sleds[i].position; movers.add(p.x, p.z, G.sled, k, id); }
    for (let i = 0; i < R.animals.length; i++) { const p = R.at[i] || R.animals[i].object.position; movers.add(p.x, p.z, G.animal, k, id); }
  }
  /** Give way: ride R's claim, points along its route with the heading there (giveway.js gap: heeded only between an
   *  animal ride and an engine, the owner 2026-11-04): an animal ride's TAIL m behind its last; an engine's from TAIL m
   *  behind its last to 4 m + LEAD s of its cruising speed ahead of the first, the whole convoy as one (they all go first). */
  function claims(R) {
    const G = R.gw, ts = R.team?.teams;
    let a = R.s - (R.team?.span ?? G.back), b = a;
    if (G.motor) {
      a = Infinity; b = -Infinity;
      for (let i = 0; ts && i < ts.length; i++) { const s = ts[i].s; if (s < a) a = s; if (s > b) b = s; } // (lazy/snowmobile.js: each one's place along the route)
      if (!(b >= a)) return; // (its teams not in yet)
      b += 4 + R.speed * LEAD; // (its cruising speed, standing too: one about to go claims its way as one going)
    }
    for (let s = a - TAIL; s <= b; s += CLAIM) { R.T.at(s, P); movers.add(P.x, P.z, CLAIM / 2, G.rank, G.id, P.h); }
  }
  // ---- VIS-267 step 3: a sleigh line's logic (every frame), its sphere and its pose (when the life pace gives it time)
  const LINE_H = 1.8; // m: a sleigh line's tallest (the herder)
  /** One frame of ride R: its logic, then its pose if `pace` (LIFE, FULL) gives its mover(s) time. */
  function move(R, dt, pace) {
    if (R.team) { R.team.place(R, dt, pace); return; } // a lazy module's teams: each its own mover (lazy/dogsled.js, snowmobile.js, horses.js)
    step(R);
    const B = ball(R), d = pace(R, B.x, B.y, B.z, B.r, LINE_H);
    if (d > 0) pose(R, d);
  }
  /** The mover a ride (or each of its lazy teams) is to the life pace, away (out of season, hidden: its next pose in
   *  view is an entry). */
  function away(R) { if (!R.team) lifePace.away(R); else for (const T of R.team.teams || []) lifePace.away(T); }
  /** The sphere round a whole sleigh line: from the herder ahead to the last sled's back. */
  function ball(R) {
    const g = R.film?.back ? -1 : 1;
    return stretchBall(R.T, R.s + (R.guide ? 2.8 : 1.2) * g, R.s - ((R.count - 1) * R.spacing + R.rear + R.towLen + 1.1) * g, 1.5 + (R.gw?.side || 0), R.ball);
  }
  /** The line's logic: where the herder walks (R.gAt: 2.2 m ahead, 0.6 m left), each animal (R.at[i]) and each towed
   *  sled's middle on the plan (R.tow[i] x, z: it follows the animal's hindquarters, a path that is only right when
   *  followed every frame). */
  function step(R) {
    const F = R.film, { T } = R, REAR = R.rear, TOW = R.towLen, g = F?.back ? -1 : 1, kr = F ? 0 : R.gw?.kr || 0;
    const at = (s, o) => { T.at(s, o); keepRight(o, kr); if (g < 0) o.h += Math.PI; return o; }; // (kept right while giving way)
    if (R.guide) { const o = at(R.s + 2.2 * g, (R.gAt ||= {})); o.x -= Math.sin(o.h) * 0.6; o.z -= Math.cos(o.h) * 0.6; } // (right of travel = (sin h, cos h))
    for (let i = 0; i < R.count; i++) {
      const A = at(R.s - i * R.spacing * g, (R.at[i] ||= {})), S = (R.tow[i] ||= at(R.s - (i * R.spacing + REAR + TOW) * g, {}));
      const qx = A.x - Math.cos(A.h) * REAR, qz = A.z + Math.sin(A.h) * REAR, dx = qx - S.x, dz = qz - S.z, d = hypot(dx, dz) || 1;
      S.x = qx - (dx / d) * TOW; S.z = qz - (dz / d) * TOW;
    }
  }
  /** The line's pose for `dt` s (all the time since its last pose): the herder, each sled pointing along its pull and
   *  settled on the snow, each animal and its clip, the ropes. */
  function pose(R, dt) {
    const F = R.film, G = R.guide, ease = F ? 1 : Math.min(1, dt * 6), REAR = R.rear;
    if (G) {
      const o = R.gAt, gp = G.g.position;
      G.update(dt, F ? F.v : dt > 0 ? Math.min(3, hypot(o.x - gp.x, o.z - gp.z) / dt) : 0, F); // its own ground speed (since its last pose)
      gp.set(o.x, o.y, o.z); G.g.rotation.y = o.h;
      G.g.updateMatrixWorld(true);
    }
    R.ropes.begin();
    for (let i = 0; i < R.count; i++) {
      const A = R.at[i], S = R.tow[i], sled = R.sleds[i];
      const dx = A.x - Math.cos(A.h) * REAR - S.x, dz = A.z + Math.sin(A.h) * REAR - S.z, d = hypot(dx, dz) || 1, h = atan2(-dz, dx); // (the hindquarters, TOW ahead)
      const fx = (dx / d) * 0.8, fz = (dz / d) * 0.8, yF = heightAt(S.x + fx, S.z + fz), yR = heightAt(S.x - fx, S.z - fz);
      S.y += ((yF + yR) / 2 - S.y) * ease; S.p = (S.p || 0) + (atan2(yF - yR, 1.6) - (S.p || 0)) * ease;
      sled.position.set(S.x, S.y, S.z); sled.rotation.set(0, h, S.p);
      sled.updateMatrixWorld(true);
      const a = R.animals[i];
      if (a) {
        a.object.position.set(A.x, A.y, A.z); a.object.rotation.y = A.h;
        if (F) a.pose('walk', Math.min(1, F.v / 0.4), F.d, F.t); // VIS-265: the walk from the metres gone
        else { a.setMode(R.v > 0.05 ? 'walk' : 'idle'); a.update(dt, R.v); }
        a.object.updateMatrixWorld(true);
        let lead = null;
        if (i === 0 && G) lead = G.hand?.getWorldPosition(tmp2) ?? null; // (VIS-53: none until the herder is in)
        else if (i > 0) lead = tmp2.set(-1.1, 0.67, 0).applyMatrix4(R.sleds[i - 1].matrixWorld);
        teamRopes(R.ropes, a, sled, lead);
      }
    }
    R.ropes.end();
  }
  /** Ride R where its logic has it now, all of it (a model arriving, Film, the editor: lazy/fine.js), told to the life pace. */
  function place(R, dt = 0) {
    if (R.film) R.tow = []; // VIS-265 Film { v, d, t, back } (film): no history (the sleds towed afresh and settled at once), turned round going back
    if (R.team) { R.team.place(R, dt, null); return; } // a lazy module's teams (null: all of them, each told as FULL)
    step(R);
    const B = ball(R); FULL(R, B.x, B.y, B.z);
    pose(R, dt);
  }
  // WS-41: a ride with `seasons` runs only in those (hidden otherwise) and restarts at the stop. VIS-87: nor while its
  // place is hidden (`off`: the editor's frame, live: setOff)
  function show(R) {
    const on = R.inSeason !== false && !R.off, back = on && !R.active;
    if (back) { toRound(R, 0); R.s = R.sStart; R.v = 0; R.wait = R.dwell * 0.4; R.travelled = 0; R.tow = []; if (R.gw) R.gw.kr = R.gw.hold = 0; }
    R.active = on;
    if (back) place(R); // VIS-267 step 3: at its start at once (out of view it is not posed: it was where it last stood)
    for (const o of R.objs) o.visible = on;
  }
  /** Ride R onto round `i` (0 = its own route, then its `rounds` in turn, wrapping): the route it drives and where on it the start is. */
  function toRound(R, i) {
    R.lap = R.rounds.length ? ((i % (R.rounds.length + 1)) + R.rounds.length + 1) % (R.rounds.length + 1) : 0;
    R.T = R.lap ? R.rounds[R.lap - 1] : R.own;
    R.sStart = startOn(R.cfg, R.T, R.open && !R.lap, spanOf(R));
  }
  function setSeason(s) {
    for (const R of live) { R.inSeason = !Array.isArray(R.cfg.seasons) || R.cfg.seasons.includes(s); show(R); }
    SEASON = s; tied?.setSeason(s);
  }
  /** VIS-87: the rides whose place `hidden(place)` says is hidden stop and go off the map (lazy/places.js); tied horses with their prop. */
  function setOff(hidden) {
    for (const R of live) { const off = !!R.cfg.place && !!hidden(R.cfg.place); if (off !== !!R.off) { R.off = off; show(R); } }
    HIDDEN = hidden; tied?.setOff(hidden);
  }
  /** VIS-265 Film (lazy/film.js only; inert unless called): ride R stands at route share `p` from its start, going at
   *  `v` m/s, `d` m on its way at Film's second `t`, `back` turned round (filmshot.js rideAt); null: it runs its own loop
   *  again from where it stands (the rest of its round to the start, then its dwell, as always). A ride with rounds is
   *  filmed on its own route (the first round). */
  function film(R, f) {
    if (R.gw) R.gw.kr = R.gw.hold = 0; // (give way: Film's ride is on the line's middle, and so is the ride it hands back)
    if (f) { R.film = f; if (R.lap) toRound(R, 0); R.s = R.sStart + f.p * R.T.len; if (R.active) place(R); return; }
    if (!R.film) return;
    R.film = null; R.v = 0; R.wait = 0;
    const k = R.s - R.sStart; R.travelled = R.T.closed ? ((k % R.T.len) + R.T.len) % R.T.len : Math.max(0, Math.min(R.T.len, k));
  }
  for (const R of live) if (!R.team) place(R); // VIS-267 step 3: every sleigh line at its start from the first picture (from here on posed in view only)
  return { count: live.length, update, rides: live, setSeason, setOff, place, scene, heightAt, film, movers, toRound, get tied() { return tied; } }; // WS-69 Life: place, scene, heightAt for lazy/fine.js rideEditor; tied: the tied horses (QA); movers: the give way board (QA); toRound: a round put on at once (QA, the editor)
}
